/**
 * The table for one game. Renders the server's snapshot as-is and picks the panel for the
 * current phase. The only client-side state is UI selection (attack targets, kept dice,
 * power changes) and whether the hand viewer is open.
 *
 * Layout: every seat across the top in seat order (starting with you), phase status in the
 * middle. In a game the seats are as large as fits on one screen, in as many rows as makes
 * their cards biggest (`useSeatFit`); seat dice are five across the card's width, whatever the
 * dice count. During a turn (`useTurnHandoff`) whoever plays — you, a bot or another player —
 * has the big seat at the bottom (card and name on top, their dice big under it, and on your
 * turn your controls, `TurnPanel`), sized to fill the screen (`useBigFit`); everyone sits small
 * along the top in seat order as slugs, the same as Portrait Layout's column (power window,
 * name and Kill Quest short name over it, pip dice), no deck, the big seat's player as a greyed
 * spot. When a turn ends its player shrinks back into their spot, then everyone goes back to
 * full seats in seat order, then the next player grows into the big seat. Seats glide between
 * layouts (`useSeatFlip`). Choosing a card happens in a modal. In a war (both layouts) the
 * seats not fighting fade out and the fighters take the room (`useFadeOut`).
 *
 * Portrait Layout (the default; `options.layout`, Layout v1 is the above): the lobby, choosing
 * and wars are laid out as above, but once play starts there are no full seats. Everyone sits in
 * one column on the right, in seat order starting with you, each cropped to its card's power
 * window (circle, frame and name; pip dice under it) and shrunk so the whole column fits on one screen (`useListFit`). Whoever plays zooms up out of
 * the column into the big seat on the left (name, power, card, dice, your buttons, stacked), and
 * their place in the column closes up; when the turn ends they collapse back into it, and the
 * moment they land the next player zooms out (`useTurnHandoff`, `useSeatFlip`).
 *
 * The turn controls are built for a thumb (this was Portrait Plus, which replaced the old Portrait
 * controls). The move of the moment (Roll before the roll, Attack after it) is one button across
 * the whole stage, with End Turn and the small tools in a row under it; the die arrows are tall
 * enough to hit, and the top row is slimmed so the card keeps its room. It is all CSS, on
 * `.side-plus` (the stage) and `.plus-mode` (the table).
 *
 * Spectators get the seats and the middle of the table only, with every card face up.
 *
 * Ghost rolls (see `Ghost.tsx`) show in the middle of the table; the lobby has the host's Game
 * Options (card selection, ghost rolls, deck).
 *
 * Beginner Mode: this client tells the server the setting (`game:tutorial`); at each reveal
 * the server pauses play for it (`GameView.waitingFor`) until every interstitial is done,
 * then this client sends `game:introDone`. Others see who play is waiting for. The teaching
 * itself is the lessons (`lessons/Lessons.tsx`): modals that pop up as each thing happens.
 *
 * Learn to Play (`view.learn`): the same table, one scripted round (see `shared/src/learn.ts`).
 * Every lesson shows, with what to do now; on your turn only the scripted move can be made, and
 * the thing to tap pulses (`lessons/coach.ts`). Round Over is the end: no game over screen, the
 * last lesson's Finish leaves the game. Forfeit is Exit Tutorial (no confirmation).
 *
 * Around a game: the lobby's Invite button shares a `/join/<id>` link, and a Quick Play lobby
 * counts down to filling with bots. Game over offers Rematch (or Join Rematch once someone opened
 * it) and the game's Recap and replay (`Review`, opened by `Main`). Emotes: the 😀 button beside
 * Chat sends one (see `EmotePicker`); each shows over its sender's seat for a few seconds
 * (watchers' at the top).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  BOT_LEVELS,
  DECK_COLORS,
  DICE_COUNT,
  MAX_PLAYERS,
  MAX_SEQUENCES,
  MAX_WAR_DICE,
  MIN_PLAYERS,
  CARD_SELECTIONS,
  DECKS,
  IDLE_LIMITS,
  WAR_REVEAL_MS,
  PLAN_LOCK_MS,
  PLAN_STEP_MS,
  applyMods,
  canKill,
  dealResponder,
  diceRule,
  freeKills,
  idleLimitText,
  sparedBy,
  t,
  tn,
  type BotLevel,
  type CardDef,
  type AttackView,
  type CardId,
  type DeckColor,
  type DieMod,
  type GameRecord,
  type GameView,
  type RollView,
  type PlayerId,
  type PublicPlayer,
  type TurnView,
} from '@kadice/shared';
import { playAttack } from '../animations/play';
import { botKillSound, forfeitSound, playSound } from '../audio';
import { PowerLine, heldTargets } from '../beginner';
import { cardImage, cardTooltip, killerName, preloadCards } from '../cards';
import type { DiceType, Options } from '../options';
import { CardRings, LessonModal, useLessons } from '../lessons/Lessons';
import { coachFor } from '../lessons/coach';
import { buzzAttack, useHaptics } from '../haptics';
import { afterRoll, rollMs, rollingDice, useRollFx, type RollFx } from '../rolling';
import { MotionDice, useMotionArm } from '../dice3d/DiceTray';
import { anim } from '../speed';
import { tx, txList } from '../text';
import { IdleBanner, IdlePill, useIdleWarnings } from './IdleCountdown';
import { usePhone, usePortraitPhone } from '../usePhone';
import { useFadeOut } from '../useFadeOut';
import type { GameClient, ShownEmote } from '../useGameClient';
import { shareLink, siteLink } from '../share';
import { Avatar } from './Avatar';
import type { ReviewMode } from './Review';
import { OptionRow, OptionTable } from './OptionTable';
import { CardZoom } from './CardZoom';
import { WithCloseX } from './CloseX';
import { formatMetric, metricName } from './Leaderboard';
import { GhostLesson, GhostPanel, useGhostLesson, useGhostReveal } from './Ghost';
import { DealOffer, DealPrompt, dealPartners, pendingDeal, useDealSeconds } from './Deals';
import { Dice, Pips } from './Dice';
import { PName } from './Names';
import { OddsModal, SeatOdds, oddsReady, type OddsContext } from './OddsTable';
import { TurnPanel, canChangeDie, fireSrc } from './TurnPanel';
import { KadiceSplash, WildLesson, WildSplash, useKadice, useWild } from './Wild';

interface Props {
  client: GameClient;
  view: GameView;
}

/** Dice kept and power changes for one roll of one turn; a stale `key` means "use defaults". */
interface DiceSelection {
  key: string;
  keep: number[];
  mods: DieMod[];
}

/** Dice saved on earlier turns stay saved, before and after the roll. They sit at the front of the pool. */
const savedIndices = (turn: TurnView) => turn.pool.slice(0, turn.pool.length - turn.rolled).map((_, i) => i);

const NO_IDS: readonly PlayerId[] = [];

/** Random play style: how long after a pick's reply to pick again if you are still asked to choose. */
const AUTO_PICK_RETRY_MS = 2_000;

export function GameTable({
  client,
  view: incoming,
  options,
  tools,
  onReview,
  onDiceType,
}: Props & {
  options: Options;
  /** Learn to Play: the dice tray's note switches dice type with this. */
  onDiceType?: (type: DiceType) => void;
  /** Phones: the header tools (icons), shown in the table's top row in place of the top bar. */
  tools?: ReactNode;
  /** Open this game's recap or replay (finished games). */
  onReview: (gameId: number, mode: ReviewMode) => void;
}) {
  const tableRef = useRef<HTMLElement>(null);
  // Another player's roll waits until the table is ready for it (`useRollGate`): `live` is the
  // game as the server sent it, held back that long.
  const { view: live, waiting: gatedRoll, drop: discard, release: releaseRoll } = useRollGate(incoming);
  const { view: shown, attack, endAttack } = useAttackReveal(live);
  const { view: warShown, busy: warBusy } = useWarReveal(shown, tableRef);
  const { view: ghostShown, busy: ghostBusy } = useGhostReveal(warShown);
  const { view, roundEnd, clearRoundEnd } = useRoundEnd(ghostShown);
  const me = view.you.id;
  // Beginner Mode screens: the ✕ on any of them skips every one left in this game.
  const [skippedFor, setSkippedFor] = useState<number | null>(null);
  const screens = options.beginnerMode && skippedFor !== view.gameId;
  // An attacker that landed straight in its seat in the column: it doesn't glide back from the big seat.
  const landedRef = useRef<PlayerId | null>(null);
  const rollFx = useRollFx(live.lastRoll);
  useHaptics(live, options.haptics);
  // Five of a kind: its splash waits for the rest to play out (the roll that made it, say), then everyone sees it.
  const wild = useWild(live, screens, !!attack || warBusy || rollFx !== null);
  // Ka'Dice: the same splash, after five of a kind's if both came at once.
  const kadice = useKadice(live, !!attack || warBusy || rollFx !== null || wild.waiting || wild.splash !== null);
  // Something is playing out on the table: no interstitial may start until it is done.
  const busy =
    !!attack ||
    warBusy ||
    rollFx !== null ||
    discard !== null ||
    wild.waiting ||
    wild.splash !== null ||
    kadice !== null;
  const killDice = useKillDice(view);
  // Idle limit: seconds left for players about to forfeit and watch (from the live view: no reveal holds it up).
  const idle = useIdleWarnings(live);
  // Play is paused for a Beginner Mode player's tutorial screens (the server rejects moves meanwhile).
  const paused = live.waitingFor.length > 0;
  const self = view.players.find((p) => p.id === me);
  const isPlayer = !!self && !view.you.spectating;

  // Card art is fetched as soon as a card is known (your hand, every face-up card), so flips
  // and the Beginner intros never wait on an image.
  useEffect(() => {
    const myColor = live.players.find((p) => p.id === live.you.id)?.color ?? null;
    preloadCards([
      ...live.players.flatMap((p) => [cardImage(p.color, 'back'), ...(p.card ? [cardImage(p.color, p.card)] : [])]),
      ...live.you.hand.map((c) => cardImage(myColor, c)),
    ]);
  }, [live]);

  // Tell the server your Beginner Mode setting, so it knows to wait for you at each reveal.
  const { request } = client;
  useEffect(() => {
    if (isPlayer) void request('game:tutorial', { on: screens });
  }, [request, isPlayer, view.gameId, screens]);
  const introDone = useCallback(() => void request('game:introDone'), [request]);
  const turn = view.phase === 'playing' ? view.turn : null;
  // A ghost roll is not a turn of play: no dice to keep, nothing to attack (see `GhostPanel`).
  const ghostTurn = !!turn?.ghost;
  const myTurn = turn?.playerId === me && !ghostTurn;
  const [viewingHand, setViewingHand] = useState(false);
  const [oddsFor, setOddsFor] = useState<PlayerId | null>(null);
  const [forfeiting, setForfeiting] = useState(false);
  const spectating = view.you.spectating;
  const [zoomOn, setZoomOn] = useState<PlayerId | null>(null);

  const reveal = useReveal(view);
  // Beginner Mode: what a ghost is, once you run out of cards. If that turn also ended the game,
  // it says you never got your roll, and game over waits for it.
  const ghostLesson = useGhostLesson(view, screens);
  const skipScreens = () => setSkippedFor(view.gameId);
  const learn = view.learn;
  // Round Over banners closed this game: the Round Over lesson follows the first.
  const [roundsOver, setRoundsOver] = useState({ gameId: view.gameId, n: 0 });
  const closeRoundEnd = () => {
    clearRoundEnd();
    setRoundsOver((r) => ({ gameId: view.gameId, n: r.gameId === view.gameId ? r.n + 1 : 1 }));
  };
  const { screen, banner } = useInterstitialScreen({
    view,
    roundEnd,
    onRoundEndDone: closeRoundEnd,
    autoNext: options.autoNext && !learn,
    onLeave: () => void client.request('lobby:leave'),
    busy,
    // Learn to Play has no game over screen: its last lesson ends it.
    holdEnd: ghostLesson.show || !!learn,
    records: client.records?.gameId === view.gameId ? client.records.records : null,
    onRematch: () => void client.request('lobby:rematch'),
    onRecap: () => onReview(view.gameId, 'recap'),
  });
  const showGhostLesson = useUnlessBusy(ghostLesson.show && !screen, busy);
  const gameEnded = live.phase === 'finished';
  const winnerName = live.players.find((p) => p.id === live.winnerId)?.name ?? null;
  // Play is waiting for you and nothing is left to show (or still to come): let it go on.
  const waitingOnMe = view.waitingFor.includes(me);
  const showWildLesson = useUnlessBusy(wild.lesson !== null && !screen, busy);
  const showing = !!screen || wild.lesson !== null;
  // Nothing flips under the round banner; cards already face up (kept from the last round, or
  // flipped by a war) stay up.
  const hidden = (p: PublicPlayer) => {
    if (spectating || p.card === null) return false;
    if (p.id === me && !p.revealed) return true;
    return banner && !reveal.faceUp.includes(upKey(p));
  };
  const targeted = useTargetedAlert(view, client.catalog, options.beginnerMode);
  const phone = usePhone();
  const { notify } = client;
  useEffect(() => {
    if (targeted && phone) {
      notify({
        kind: 'error',
        text: `${t('targeted.title', { name: targeted.name })} ${t('targeted.body', { quest: targeted.quest })}`,
      });
    }
  }, [targeted, phone, notify]);

  // Attack targets belong to one turn; a stale selection is simply ignored.
  const turnKey = turn ? `${view.round}:${view.sequence}:${turn.playerId}:${turn.extra}:${turn.bonus}` : '';
  const [selection, setSelection] = useState<{ key: string; ids: PlayerId[] }>({ key: '', ids: [] });
  const canTarget = (p: PublicPlayer) => myTurn && p.id !== me && p.hasCard;

  // Kept dice belong to one roll; they reset (to the saved dice) whenever the pool changes.
  const diceKey = turn ? `${turnKey}:${turn.hasRolled}:${turn.pool.join()}` : '';
  const [diceSel, setDiceSel] = useState<DiceSelection>({ key: '', keep: [], mods: [] });
  const dice: DiceSelection =
    diceSel.key === diceKey ? diceSel : { key: diceKey, keep: turn ? savedIndices(turn) : [], mods: [] };

  // Only kept dice count: a die changed by a power must also be kept before it can kill.
  const keptValues = myTurn && turn ? dice.keep.map((i) => applyMods(turn.pool, dice.mods)[i]!) : [];
  const cardOf = (p: PublicPlayer) => (p.card === null ? undefined : client.catalog.get(p.card));
  /** How a seat's card shows on the table (face down until flipped, see `hideMine` / `hideEnemies`). */
  const tableCard = (p: PublicPlayer) => (hidden(p) ? { ...p, card: null } : p);
  const zoomPlayer = view.players.find((p) => p.id === zoomOn && p.card !== null);
  const zoomCard = zoomPlayer && cardOf(zoomPlayer);
  const killable = (p: PublicPlayer) => {
    const def = cardOf(p);
    return canTarget(p) && !!def && keptValues.length > 0 && canKill(keptValues, def.killer);
  };
  const free = myTurn && self ? freeKills(cardOf(self)?.power, keptValues) : 0;
  const freeKillable = (p: PublicPlayer) => free > 0 && canTarget(p) && !killable(p);
  // Only cards your kept dice (or free kills) can destroy may be targeted: no attacks that miss.
  const targetable = (p: PublicPlayer) => killable(p) || freeKillable(p);
  // Drop targets that can no longer be hit (card gone, or the kept dice changed).
  const targets =
    selection.key === turnKey
      ? selection.ids.filter((id) => view.players.some((p) => p.id === id && targetable(p)))
      : [];
  // Learn to Play: your scripted turn's next step (only it can be made; see `lessons/coach.ts`).
  const coach =
    learn && myTurn && turn && isPlayer ? coachFor(learn, turn, view.sequence, dice.keep, dice.mods, targets) : null;
  const toggleTarget = (id: PlayerId) =>
    setSelection({ key: turnKey, ids: targets.includes(id) ? targets.filter((t) => t !== id) : [...targets, id] });
  const highlight = options.highlightAttackable;
  // Beginner Mode: while the dice on the table (held or not, power changes included) could destroy
  // a card, that card burns in its deck colour (`Seat killFire`), and ending your turn asks first
  // (`TurnPanel`'s kill warning).
  const tableValues = myTurn && turn?.hasRolled ? applyMods(turn.pool, dice.mods) : [];
  const tableFree = tableValues.length > 0 && self ? freeKills(cardOf(self)?.power, tableValues) : 0;
  const couldKill = (p: PublicPlayer) => {
    const def = cardOf(p);
    return canTarget(p) && !!def && tableValues.length > 0 && (tableFree > 0 || canKill(tableValues, def.killer));
  };
  // Beginner Mode: a sound when enemies first light up as attackable.
  const anyAttackable = highlight && view.players.some((p) => targetable(p));
  const wasAttackable = useRef(false);
  useEffect(() => {
    if (anyAttackable && !wasAttackable.current && options.beginnerMode) playSound('destroyIt');
    wasAttackable.current = anyAttackable;
  }, [anyAttackable, options.beginnerMode]);

  // Random play style: pick a card for you whenever one is needed (once per prompt). A pick that
  // didn't take (lost on a dropped connection, say) leaves the prompt up: try again shortly.
  const autoPicked = useRef('');
  const [pickTries, setPickTries] = useState(0);
  const random = options.playStyle === 'random' && view.you.mustChoose && view.you.hand.length > 0;
  const chooseKey = `${view.phase}:${view.round}:${view.sequence}:${view.you.hand.join()}:${pickTries}`;
  useEffect(() => {
    if (!random || autoPicked.current === chooseKey) return;
    autoPicked.current = chooseKey;
    const hand = view.you.hand;
    void request('game:chooseCard', { cardId: hand[Math.floor(Math.random() * hand.length)]!, random: true }).then(
      () => {
        setTimeout(() => setPickTries((n) => n + 1), AUTO_PICK_RETRY_MS);
      },
    );
  }, [random, chooseKey, request, view.you.hand]);

  // Every seat (you included) in seat order, starting with you, so you are always first on the
  // left (spectators: starting at the dealer). The dealer chip shows where the round starts.
  const n = view.players.length;
  const firstSeat =
    (spectating ? undefined : self?.seat) ?? view.players.find((p) => p.id === view.dealerId)?.seat ?? 0;
  const allSeats = [...view.players].sort((a, b) => ((a.seat - firstSeat + n) % n) - ((b.seat - firstSeat + n) % n));
  // A war: everyone not fighting fades out, then leaves the layout so the fighters get the room;
  // they fade back in when it is over (`useFadeOut`).
  const war = view.phase === 'war' ? view.war : null;
  const warOut = useFadeOut(
    war ? allSeats.filter((p) => !war.participants.includes(p.id)).map((p) => p.id) : NO_IDS,
    CARD_FLY_MS,
  );
  const seats = warOut.gone.length > 0 ? allSeats.filter((p) => !warOut.gone.includes(p.id)) : allSeats;
  // Lobby, portrait phones: three seats to a row (and three cropped seats to a row in a turn).
  const portrait = usePortraitPhone();
  // A turn: whoever plays has the big seat at the bottom, the rest sit small and cropped along
  // the top in seat order, with a greyed spot (name and Kill Quest only) where the player of the
  // turn sits. Between turns (and in wars, ghost rolls and Beginner pauses) every seat is full
  // size, as large as fits on one screen with the middle panel (see `useSeatFit`). See `useTurnHandoff`.
  // An attack keeps its attacker in the big seat: the animation starts from there, and they
  // shrink back into their place only once it is done.
  const fitted = view.phase !== 'lobby';
  // Portrait Layout, in play: the big seat on the left, everyone else in a column on the right,
  // stacked (name above the card, no power line). Its thumb-sized turn controls are CSS only
  // (`.side-plus`, `.plus-mode`; the names are from when it was Portrait Plus).
  const sideLayout = options.layout === 'portrait' && view.phase === 'playing';
  // Desktop (not phone-sized): no slugs. Layout v1's turn: the player's own seat grows in its place
  // in the row (`inRow`); Portrait Layout's column is a grid of full seats.
  const roomy = !phone;
  // Layout v1 on phones: when a turn ends, its player shrinks back into their spot before anyone else moves.
  // Portrait Layout on phones: the column makes room for them first, then they glide into it.
  const { big, back, room, returned } = useTurnHandoff(
    view,
    paused,
    attack?.attackerId ?? null,
    options.layout === 'v1' && !roomy,
    sideLayout && !roomy,
    landedRef,
  );
  // An attack plays only once the table is still: its attacker settled in the big seat (the last
  // player back in their place, the attacker grown into it) and no roll playing (motion dice included).
  const attackSettled = !!attack && big === attack.attackerId && back === null && room === null && rollFx === null;
  // Another player's roll is held (`useRollGate`) until every animation is done (an attack, the
  // last turn's seat going home), their seat has landed in the big seat (it grows for
  // `CARD_FLY_MS` once it has it), and `ROLL_GATE_MS` more.
  const bigSince = useRef({ id: big, at: 0 });
  useLayoutEffect(() => {
    if (bigSince.current.id !== big) bigSince.current = { id: big, at: performance.now() };
  }, [big]);
  const rollReady = !!gatedRoll && !busy && big === gatedRoll.playerId && back === null && room === null;
  useEffect(() => {
    if (!rollReady || !gatedRoll) return;
    const left = bigSince.current.at + CARD_FLY_MS + ROLL_GATE_MS - performance.now();
    const timer = setTimeout(() => releaseRoll(gatedRoll.seq), Math.max(0, left));
    return () => clearTimeout(timer);
  }, [rollReady, gatedRoll, releaseRoll]);
  useAttackAnimation(
    tableRef,
    attack,
    attackSettled,
    endAttack,
    me,
    shown,
    live,
    landedRef,
    options.haptics && !view.you.spectating,
  );
  // Allow Bot Collusion: the deal waiting on an answer (from the live view: nothing holds it up).
  const pending = pendingDeal(live);
  const dealSeconds = useDealSeconds(live);
  const [offering, setOffering] = useState(false);
  const canOffer =
    view.settings.botCollusion &&
    myTurn &&
    !!turn?.hasRolled &&
    !turn.bonus &&
    !turn.offered &&
    !pending &&
    dealPartners(view).length > 0;
  const dealWait =
    pending && pending.from === me && dealResponder(pending) !== me
      ? { name: view.players.find((p) => p.id === pending.to)?.name ?? '?', seconds: dealSeconds }
      : null;
  const spared = sparedBy(view.deals, me, view.sequence >= MAX_SEQUENCES);
  const dealPartner = (p: PublicPlayer) =>
    view.deals.some((d) => d.status === 'active' && [d.from, d.to].includes(me) && [d.from, d.to].includes(p.id));
  const bigPlayer = fitted && big !== null ? view.players.find((p) => p.id === big) : undefined;
  // Portrait: the turn's player, still in the big seat while their place opens a gap.
  const roomPlayer = sideLayout && room !== null ? view.players.find((p) => p.id === room) : undefined;
  // Layout v1: the turn's player stepping back, their big seat shrinking into their spot.
  const backPlayer = fitted && !sideLayout && back !== null ? view.players.find((p) => p.id === back) : undefined;
  const shownBig = bigPlayer ?? backPlayer;
  useShrinkToSpot(tableRef, backPlayer?.id ?? null);
  // Layout v1's slug row: during a turn, and while its player steps back into it.
  const stripsUp = !sideLayout && fitted && (!!bigPlayer || back !== null);
  const slugs = stripsUp && !roomy;
  // Desktop, Layout v1 in a turn: no big seat at the bottom; the player's seat in the row is it.
  const inRow = stripsUp && roomy;
  const showArea = isPlayer && myTurn && bigPlayer?.id === me;
  const fit = useSeatFit(tableRef, fitted && !slugs && !sideLayout ? seats.length : 0, view.phase === 'war', inRow);
  // Sized for every seat (not one fewer during a turn), so the column keeps its size between turns.
  const list = useListFit(tableRef, sideLayout ? seats.length : 0, roomy);
  const listW = list?.width ?? null;
  const bigCard = useBigFit(tableRef, !!(shownBig ?? roomPlayer) && !inRow, sideLayout, listW);
  const stripRows = Math.ceil(seats.length / (portrait ? 3 : 5));
  const rows = slugs
    ? splitRows(seats, stripRows)
    : fitted
      ? splitRows(seats, fit?.rows ?? 1)
      : portrait && seats.length > 3
        ? [seats.slice(0, 3), seats.slice(3)]
        : [seats];
  const stripCols = Math.ceil(seats.length / Math.max(1, stripRows));
  // Layout v1 on a portrait phone with three players or fewer: one slug row with room to spare,
  // so the slugs show their whole card (and the spot is card-shaped).
  const fullStrips = slugs && portrait && allSeats.length <= 3;

  // Debug Mode: kill-odds readout beside every face-up card.
  const oddsCx: OddsContext = {
    view,
    catalog: client.catalog,
    order: allSeats.map((p) => p.id),
    kept: myTurn && turn?.hasRolled ? keptValues : null,
  };
  const showOdds = options.debug && options.debugStats && oddsReady(oddsCx);
  const oddsPlayer = showOdds ? view.players.find((p) => p.id === oddsFor) : undefined;

  const inProgress = view.phase !== 'lobby' && view.phase !== 'finished';
  // Game over: a player of this game can open its rematch, or join the one already opened.
  const canRematch = view.phase === 'finished' && !!self;
  // Emotes: the latest from each player at this table; a watcher's (no seat) shows at the top.
  const emoteOf = (id: PlayerId) => client.emotes.find((e) => e.playerId === id);
  const seatIds = new Set(view.players.map((p) => p.id));
  const looseEmotes = client.emotes.filter((e) => !seatIds.has(e.playerId));
  const leave = () => {
    if (inProgress && !spectating) setForfeiting(true);
    else void client.request('lobby:leave');
  };

  // A bot's announced move: its seat shows the dice it keeps (after power changes) lifted, and its targets marked.
  const plan = turn?.plan ?? null;
  const planKey = plan && turn ? `${turn.playerId}:${JSON.stringify(plan)}` : null;
  // A power change plays out first (`usePlanSteps`); the dice lock a moment after it is done (`PLAN_LOCK_MS`).
  const planSteps = usePlanSteps(planKey, turn?.pool ?? NO_DICE, plan?.mods ?? NO_MODS, !!turn?.wild);
  const planLocked = usePlanLock(planKey, planSteps.steps);
  const planKeep = plan && planLocked ? plan.keep : undefined;
  // Someone else's turn, in the big seat: the dice their power can change burn while they decide.
  // A bot's announced move puts out every flame but those of the dice it changes, each going out
  // once that die is set.
  const burningFor = (p: PublicPlayer): readonly number[] => {
    if (!turn || turn.playerId !== p.id || !turn.hasRolled || p.color === null) return NO_DICE;
    const rule = diceRule(turn.pool, cardOf(tableCard(p))?.power, turn.wild);
    const firstFresh = turn.pool.length - turn.rolled;
    const burning = turn.pool.map((_, i) => i).filter((i) => canChangeDie(rule, turn.pool[i]!, i, firstFresh));
    return plan ? burning.filter((i) => planSteps.burning.includes(i)) : burning;
  };
  // Held dice stay coloured as held until the turn that drops them ends: before the roll all of
  // them, after it the ones carried into the pool (the fresh ones follow at its end).
  const seatDice = (
    p: PublicPlayer,
  ): { values: number[] | readonly number[]; rolling: number[]; held: number[]; dropping?: number[] } => {
    const all = (n: number) => Array.from({ length: n }, (_, i) => i);
    // A war: the fighters' rolls sit under their cards (see `Seat`), landing one die at a time.
    if (view.phase === 'war' && view.war?.participants.includes(p.id)) {
      const rolled = view.war.rolls[p.id];
      if (!rolled) return { values: [], rolling: [], held: [] };
      return { ...rollingDice(rolled, 0, rollFx?.war && rollFx.playerId === p.id ? rollFx : null), held: [] };
    }
    // A kill's victims lost their dice (they flew off in the attack) for the rest of the round.
    if (turn?.playerId !== p.id && killDice.victims.includes(p.id)) return { values: [], rolling: [], held: [] };
    // A kill's dice stay on the attacker's seat for the rest of the round (they hold none after it).
    const kill = killDice.dice[p.id];
    if (turn?.playerId !== p.id && p.held.length === 0 && kill) return { values: kill, rolling: [], held: [] };
    if (turn?.playerId !== p.id) return { values: p.held, rolling: [], held: all(p.held.length) };
    // About to roll with held dice thrown back: those unlock and drop (`useRollGate`) before the roll shows.
    if (discard?.playerId === p.id && !turn.hasRolled) {
      return {
        values: p.held,
        rolling: [],
        held: all(p.held.length).filter((i) => !discard.indices.includes(i)),
        dropping: discard.indices,
      };
    }
    const pool = turn.hasRolled ? turn.pool : turn.ghost ? [] : p.held;
    const values = plan ? applyMods(pool, plan.mods) : pool;
    // A fresh roll lands one die at a time.
    const fx = turn.hasRolled && rollFx?.playerId === p.id && !rollFx.war ? rollFx : null;
    const held = all(turn.hasRolled ? pool.length - turn.rolled : pool.length);
    return { ...rollingDice(values, values.length - turn.rolled, fx), held };
  };

  // Seats glide between layouts; the dealer chip slides to the new dealer between rounds.
  // (`layout` also names a turn's player stepping back, and who sits out a war.)
  const layout = `${sideLayout ? 'side' : 'table'}:${bigPlayer?.id ?? (back !== null ? `back:${back}` : roomPlayer ? `room:${roomPlayer.id}` : '')}:${warOut.gone.join(',')}`;
  useSeatFlip(tableRef, layout, landedRef);
  useChipFly(tableRef, view.dealerId);
  // Anything moving on the table (a roll, an attack, a war or ghost reveal, the seats changing
  // layout): every control on the table is locked (inert) until it is done. The header tools stay usable.
  const cardFlying = useCardInFlight(layout);
  const locked = busy || ghostBusy || cardFlying;
  // Lessons (Beginner Mode, or Learn to Play): one modal at a time, as each thing happens. A
  // turn lesson waits for your big seat to assemble.
  const lessons = useLessons(
    {
      view,
      enabled: screens || !!learn,
      myTurn: showArea && !cardFlying,
      couldKill: view.players.some(couldKill),
      coach: coach?.stage ?? null,
      roundsOver: roundsOver.gameId === view.gameId ? roundsOver.n : 0,
    },
    busy || locked || !!screen || wild.lesson !== null || showGhostLesson,
  );
  // A lesson up or due (they show once the table is still) holds the reveal too.
  const lessonDue = lessons.pending;
  useEffect(() => {
    if (waitingOnMe && !showing && !lessonDue && !busy) introDone();
  }, [waitingOnMe, showing, lessonDue, busy, introDone]);
  const finishLearn = () => void client.request('lobby:leave');
  // Beginner Mode: no turn buttons until every tutorial screen (up or still to come) is done.
  const tutoring = (screens || !!learn) && (showing || paused || lessons.pending || showGhostLesson || showWildLesson);
  // Rings round cards: the lesson's subject, or (Learn to Play) the cards to tap.
  const rings =
    lessons.lesson === 'vuln'
      ? null
      : lessons.lesson === 'power' || lessons.lesson === 'powerUse'
        ? [me]
        : !lessons.lesson && coach
          ? coach.targets
          : [];

  // One seat: full size, a slug (`strip`: everyone but the player of a turn, see `Seat`),
  // or the big seat (whoever plays, with their dice, or your controls on your turn, in `diceArea`).
  // `leaving`: Layout v1, the turn is over; the big seat shrinks into the spot (`useShrinkToSpot`).
  // `gap`: Portrait Layout, the column making room for the turn's player (an unseen slug).
  const seatFor = (p: PublicPlayer, size: SeatSize | 'gap', diceArea?: ReactNode, leaving = false) => (
    <Seat
      key={`${p.id}:${size === 'name' || size === 'spot' || size === 'gap' ? size : 'seat'}`}
      size={size === 'gap' ? 'strip' : size}
      gap={size === 'gap'}
      leaving={leaving}
      fade={
        warOut.fading.includes(p.id)
          ? 'out'
          : warOut.back.includes(p.id) || (p.id === returned && (size === 'strip' || size === 'normal'))
            ? 'in'
            : undefined
      }
      diceArea={diceArea}
      side={size === 'big' && sideLayout}
      full={roomy}
      player={tableCard(p)}
      isYou={p.id === me}
      card={cardOf(tableCard(p))}
      onZoom={tableCard(p).card !== null ? () => setZoomOn(p.id) : undefined}
      targeting={targeted?.id === p.id}
      idleSeconds={idle.get(p.id)}
      deal={
        p.id === me ? undefined : dealPartner(p) ? 'partner' : view.betrayers.includes(p.id) ? 'betrayer' : undefined
      }
      emote={emoteOf(p.id)}
      voice={client.voice.speaking.has(p.id) ? 'speaking' : client.voice.members.includes(p.id) ? 'in' : undefined}
      isTurn={size === 'name' || turn?.playerId === p.id}
      isDealer={view.dealerId === p.id}
      isWinner={view.winnerId === p.id}
      inLobby={view.phase === 'lobby'}
      selected={targets.includes(p.id) || !!plan?.targets.includes(p.id)}
      killable={highlight && killable(p)}
      freeKillable={highlight && freeKillable(p)}
      killFire={options.beginnerMode && couldKill(p)}
      dice={seatDice(p).values}
      atWar={view.phase === 'war' && !!view.war?.participants.includes(p.id)}
      rolling={seatDice(p).rolling}
      held={seatDice(p).held}
      dropping={seatDice(p).dropping}
      lifted={turn?.playerId === p.id ? planKeep : undefined}
      odds={showOdds && p.card !== null ? oddsCx : null}
      onOdds={() => setOddsFor(p.id)}
      handCount={p.id === me && !spectating ? view.you.hand.length : undefined}
      onViewHand={p.id === me && !spectating && view.phase !== 'lobby' ? () => setViewingHand(true) : undefined}
      onSelect={
        size !== 'gap' && targetable(p) && (!coach || coach.targets.includes(p.id))
          ? () => toggleTarget(p.id)
          : undefined
      }
      onRemove={
        p.bot && view.phase === 'lobby' && view.ownerId === me
          ? () => void client.request('lobby:removeBot', { playerId: p.id })
          : undefined
      }
    />
  );
  // The big seat's dice: your controls on your turn, anyone else's dice (big, held ones locked).
  const bigDice = !bigPlayer ? null : showArea && turn ? (
    <TurnPanel
      key={diceKey}
      client={client}
      turn={turn}
      card={self && cardOf(self)}
      keep={dice.keep}
      mods={dice.mods}
      onDiceChange={(keep, mods) => setDiceSel({ key: diceKey, keep, mods })}
      targets={targets.map((id) => view.players.find((p) => p.id === id)!)}
      free={free}
      debug={options.debug && !learn}
      tips={options.beginnerMode && !learn}
      rollFx={rollFx?.playerId === me && !rollFx.war ? rollFx : null}
      paused={paused}
      locked={locked}
      hideControls={tutoring}
      coach={coach}
      killWarn={options.beginnerMode && !learn && view.players.some(couldKill)}
      onDeal={canOffer ? () => setOffering(true) : undefined}
      dealWait={dealWait}
      spared={spared}
    />
  ) : (
    <BigDice
      {...seatDice(bigPlayer)}
      lifted={turn?.playerId === bigPlayer.id ? planKeep : undefined}
      fire={bigPlayer.color}
      burning={burningFor(bigPlayer)}
      steps={turn?.playerId === bigPlayer.id ? planSteps : undefined}
    />
  );

  return (
    <MotionDice
      live={live}
      haptics={options.haptics && !view.you.spectating}
      note={
        learn && onDiceType
          ? (cancel) => (
              <DiceNote
                onPick={(type) => {
                  cancel();
                  onDiceType(type);
                }}
              />
            )
          : undefined
      }
    >
      <section
        className={`table${fitted ? ' fitted' : ''}${bigPlayer ? ' turn-layout' : ''}${sideLayout ? ' side-mode plus-mode' : ''}`}
        ref={tableRef}
      >
        {live.phase === 'playing' && <DealPrompt view={live} client={client} beginner={options.beginnerMode} />}
        {offering && canOffer && turn && (
          <DealOffer
            view={view}
            client={client}
            catalog={client.catalog}
            power={(self && cardOf(self)?.power) ?? null}
            dice={tableValues}
            kept={keptValues}
            beginner={options.beginnerMode}
            onClose={() => setOffering(false)}
          />
        )}
        <div className="row table-top">
          <h2>{phaseTitle(view)}</h2>
          {spectating && <span className="badge">{t('table.watchingBadge')}</span>}
          <span className="spacer" />
          {view.spectators.length > 0 && (
            <span className="muted small">
              {t('table.spectators', { names: view.spectators.join(t('list.separator')) })}
            </span>
          )}
          {canRematch && !learn && (
            <button onClick={() => onReview(view.gameId, 'recap')} disabled={locked}>
              {t('end.recap')}
            </button>
          )}
          {canRematch && !learn && (
            <button className="primary" onClick={() => void client.request('lobby:rematch')} disabled={locked}>
              {t(view.rematchId ? 'end.joinRematch' : 'end.rematch')}
            </button>
          )}
          <button onClick={learn ? finishLearn : leave} disabled={!learn && locked}>
            {learn
              ? t('learn.exit')
              : view.phase === 'finished'
                ? t('table.backToLobby')
                : spectating
                  ? t('table.stopWatching')
                  : inProgress
                    ? t('table.forfeit')
                    : t('table.leave')}
          </button>
          {tools}
        </div>

        {/* Portrait Layout, in play: the big seat (or, with no turn up, the middle panel) left, the column right. */}
        {sideLayout && (
          <div
            className="side-layout side-plus"
            style={listW ? ({ '--list-w': `${listW}px` } as CSSProperties) : undefined}
          >
            <div className="side-stage" inert={locked}>
              {bigPlayer ? (
                <div
                  className="big-wrap"
                  style={bigCard ? ({ '--big-card': `${bigCard}px` } as CSSProperties) : undefined}
                >
                  {seatFor(bigPlayer, 'big', bigDice)}
                </div>
              ) : roomPlayer ? (
                <div
                  className="big-wrap"
                  style={bigCard ? ({ '--big-card': `${bigCard}px` } as CSSProperties) : undefined}
                >
                  {seatFor(roomPlayer, 'big', <BigDice {...seatDice(roomPlayer)} lifted={undefined} />)}
                </div>
              ) : (
                // Ghost rolls and Beginner pauses say what is happening; a turn handing over says nothing.
                (ghostTurn || paused || !turn) && (
                  <div className="table-center">
                    <PhasePanel client={client} view={view} botLevel={options.botLevel} rollFx={rollFx} />
                  </div>
                )
              )}
            </div>
            {/* Whoever has the big seat has no place here while they play: their name sits above their card. */}
            {/* Desktop: full seats in a grid, the big seat's place a greyed full-size spot. */}
            <div
              className={`opponents side-list${roomy ? ' roomy' : ''}`}
              inert={locked}
              style={list?.card ? ({ '--fit-card': `${list.card}px` } as CSSProperties) : undefined}
            >
              {seats
                .filter((p) => roomy || p.id !== bigPlayer?.id)
                .map((p) =>
                  seatFor(
                    p,
                    p.id === bigPlayer?.id ? 'spot' : p.id === roomPlayer?.id ? 'gap' : roomy ? 'normal' : 'strip',
                  ),
                )}
            </div>
          </div>
        )}

        {!sideLayout &&
          rows.map((row, r) => (
            <div
              key={r}
              inert={locked}
              className={`opponents${slugs ? ` strips${fullStrips ? ' full' : ''}` : fitted ? ' fit' : rows.length > 1 ? ' multi' : ''}${view.phase === 'war' ? ' war' : ''}`}
              style={
                slugs
                  ? ({ '--cols': stripCols } as CSSProperties)
                  : fitted && fit
                    ? ({ '--fit-card': `${fit.card}px` } as CSSProperties)
                    : undefined
              }
            >
              {row.map((p) =>
                p.id === shownBig?.id
                  ? inRow
                    ? seatFor(p, 'big', bigDice)
                    : seatFor(p, 'spot', undefined, p.id === backPlayer?.id)
                  : seatFor(p, slugs ? 'strip' : 'normal'),
              )}
            </div>
          ))}

        {/* The middle panel says whose turn it is between turns; in a turn the big seat says so. */}
        {!stripsUp && !sideLayout && (
          <div className="table-center" inert={locked}>
            <PhasePanel client={client} view={view} botLevel={options.botLevel} rollFx={rollFx} />
          </div>
        )}

        {/* A turn: whoever plays, big at the bottom: card, name and power beside it, their dice (or your controls) under them. */}
        {/* Stepping back (Layout v1): the same seat, shrinking into its spot in the slug row. */}
        {shownBig && !sideLayout && !inRow && (
          <div
            className="big-wrap"
            inert={locked}
            style={bigCard ? ({ '--big-card': `${bigCard}px` } as CSSProperties) : undefined}
          >
            {bigPlayer
              ? seatFor(bigPlayer, 'big', bigDice)
              : seatFor(shownBig, 'big', <BigDice {...seatDice(shownBig)} lifted={undefined} />, true)}
          </div>
        )}

        {idle.has(me) && !spectating && <IdleBanner seconds={idle.get(me)!} />}

        {looseEmotes.length > 0 && (
          <div className="emote-float" aria-live="polite">
            {looseEmotes.map((e) => (
              <span key={e.id} className="emote-bubble loose">
                <strong>{e.name}</strong> {t(`emote.${e.emote}`)}
              </span>
            ))}
          </div>
        )}

        {targeted && !phone && (
          <div key={targeted.key} className="targeted-banner" role="alert">
            <strong>{tx('targeted.title', { name: <PName>{targeted.name}</PName> })}</strong>
            <span>{t('targeted.body', { quest: targeted.quest })}</span>
          </div>
        )}

        {screen}
        {wild.splash && <WildSplash view={live} wild={wild.splash} />}
        {kadice && <KadiceSplash view={live} kadice={kadice} />}
        {/* Lessons stay usable while the table is locked (a bot rolling under one). */}
        {rings && rings.length === 0 ? null : <CardRings ids={rings} />}
        {lessons.lesson &&
          (learn ? (
            <LessonModal
              key={lessons.lesson}
              id={lessons.lesson}
              learn
              last={lessons.lesson === 'win'}
              onNext={lessons.lesson === 'win' ? finishLearn : lessons.done}
            />
          ) : (
            <WithCloseX key={lessons.lesson} onClose={skipScreens}>
              <LessonModal id={lessons.lesson} learn={false} card={self && cardOf(self)} onNext={lessons.done} />
            </WithCloseX>
          ))}
        {/* Overlays opened from the table lock with it (`display: contents`: no box of its own). */}
        <div className="table-overlays" inert={locked}>
          {/* Beginner screens: the circle ✕ on each skips every one left this game. */}
          {showWildLesson && wild.lesson && (
            <WithCloseX onClose={skipScreens}>
              <WildLesson view={live} wild={wild.lesson} catalog={client.catalog} onDone={wild.lessonDone} />
            </WithCloseX>
          )}
          {showGhostLesson && (
            <WithCloseX onClose={skipScreens}>
              <GhostLesson onDone={ghostLesson.done} ended={gameEnded} winner={winnerName} />
            </WithCloseX>
          )}

          {view.you.mustChoose && options.playStyle === 'choose' && (
            <HandModal
              title={t('hand.chooseTitle')}
              color={self?.color ?? null}
              hand={view.you.hand}
              catalog={client.catalog}
              onChoose={(cardId) => void client.request('game:chooseCard', { cardId })}
            />
          )}
          {forfeiting && (
            <Splash onClose={() => setForfeiting(false)}>
              <h2>{t('forfeit.title')}</h2>
              <p>{t('forfeit.body')}</p>
              <div className="row actions">
                <button onClick={() => setForfeiting(false)}>{t('common.cancel')}</button>
                <button
                  onClick={() => {
                    setForfeiting(false);
                    playSound(forfeitSound());
                    void client.request('lobby:leave');
                  }}
                >
                  {t('forfeit.leave')}
                </button>
                <button
                  className="primary"
                  onClick={() => {
                    setForfeiting(false);
                    playSound(forfeitSound());
                    void client.request('lobby:watch', { gameId: view.gameId });
                  }}
                >
                  {t('forfeit.watch')}
                </button>
              </div>
            </Splash>
          )}
          {zoomPlayer && zoomCard && (
            <CardZoom
              player={zoomPlayer}
              card={zoomCard}
              isYou={zoomPlayer.id === me}
              dice={seatDice(zoomPlayer)}
              beginner={options.beginnerMode}
              onClose={() => setZoomOn(null)}
            />
          )}
          {oddsPlayer && <OddsModal cx={oddsCx} player={oddsPlayer} onClose={() => setOddsFor(null)} />}
          {viewingHand && !view.you.mustChoose && (
            <HandModal
              title={t('hand.title')}
              color={self?.color ?? null}
              hand={view.you.hand}
              discards={view.you.discard}
              catalog={client.catalog}
              onClose={() => setViewingHand(false)}
            />
          )}
        </div>
      </section>
    </MotionDice>
  );
}

/** Another player's roll: once their seat has landed in the big seat, it waits this long more (decision, user 2026-10-16). */
const ROLL_GATE_MS = 1_000;
/** A held roll shows after this anyway (nothing can hold the table up for good). */
const ROLL_GATE_MAX_MS = 12_000;
/** Another player's held dice they roll again: unlocked and dropped this long before the roll starts. */
const DISCARD_DROP_MS = 1_100;

/** The held dice (indexes into the roller's `held` in `before`) that `next`'s roll threw back: not carried into its pool. */
function discardedHeld(before: GameView, next: GameView): number[] {
  const roll = next.lastRoll;
  const held = before.players.find((p) => p.id === roll?.playerId)?.held;
  if (!roll || !held || before.turn?.playerId !== roll.playerId || before.turn.hasRolled || !next.turn) return [];
  const kept = next.turn.pool.slice(0, next.turn.pool.length - next.turn.rolled);
  return held.flatMap((v, i) => {
    const at = kept.indexOf(v);
    if (at < 0) return [i];
    kept.splice(at, 1);
    return [];
  });
}

/**
 * Holds back another player's roll (not yours, not a war or ghost roll) until the table is ready
 * for it, so nobody's roll ever starts while an attack plays or before their seat has landed in
 * the big seat: `waiting` is that roll while it is held, and `GameTable` calls `release(seq)` once
 * the table is ready (after `ROLL_GATE_MAX_MS` it shows anyway). Views that come in meanwhile
 * queue behind it and follow it with the gaps they came in with, so what happens after the roll
 * (a bot's plan, its attack) keeps its pace. A roll that throws held dice back shows that first
 * (`drop`: the roller and those held dice, unlocked and falling, for `DISCARD_DROP_MS`); the roll
 * itself (classic or 3D) starts only after it.
 */
function useRollGate(view: GameView): {
  view: GameView;
  waiting: RollView | null;
  drop: { playerId: PlayerId; indices: number[] } | null;
  release: (seq: number) => void;
} {
  const [gate, setGate] = useState<{
    last: GameView;
    shown: GameView;
    queue: GameView[];
    drop: { seq: number; playerId: PlayerId; indices: number[] } | null;
  }>(() => ({
    last: view,
    shown: view,
    queue: [],
    drop: null,
  }));
  // When each view came in (`performance.now()`), for the gaps between queued views.
  const arrived = useRef(new WeakMap<GameView, number>());
  useLayoutEffect(() => {
    arrived.current.set(view, performance.now());
  }, [view]);
  const held = (next: GameView, before: GameView): RollView | null => {
    const roll = next.lastRoll;
    if (!roll || roll.seq <= (before.lastRoll?.seq ?? 0) || next.gameId !== before.gameId) return null;
    if (roll.playerId === next.you.id || roll.war || roll.ghost || next.phase !== 'playing') return null;
    return next.turn?.playerId === roll.playerId && !next.turn.ghost ? roll : null;
  };
  // Used in this render too, so nothing downstream sees a held roll for even one pass.
  let current = gate;
  if (gate.last !== view) {
    current =
      gate.queue.length > 0 || held(view, gate.shown)
        ? { ...gate, last: view, queue: [...gate.queue, view] }
        : { last: view, shown: view, queue: [], drop: null };
    setGate(current);
  }
  const head = current.queue[0];
  const shown = current.shown;
  const drop = current.drop;
  const waiting = head && !drop ? held(head, shown) : null;
  // A queued view that isn't a held roll follows the one before it, as far apart as they came in.
  // A held roll waits for `release`, or `ROLL_GATE_MAX_MS`; once released with dice to drop, for the drop.
  useEffect(() => {
    if (!head) return;
    const gap = (arrived.current.get(head) ?? 0) - (arrived.current.get(shown) ?? 0);
    const timer = setTimeout(
      () => setGate((g) => (g.queue[0] === head ? { ...g, shown: head, queue: g.queue.slice(1), drop: null } : g)),
      drop ? anim(DISCARD_DROP_MS) : waiting ? ROLL_GATE_MAX_MS : Math.max(0, gap),
    );
    return () => clearTimeout(timer);
  }, [head, shown, waiting, drop]);
  const release = useCallback(
    (seq: number) =>
      setGate((g) => {
        const next = g.queue[0];
        if (next?.lastRoll?.seq !== seq || g.drop) return g;
        const indices = discardedHeld(g.shown, next);
        return indices.length > 0
          ? { ...g, drop: { seq, playerId: next.lastRoll.playerId, indices } }
          : { ...g, shown: next, queue: g.queue.slice(1), drop: null };
      }),
    [],
  );
  return { view: shown, waiting, drop, release };
}

/**
 * When an attack lands, keep showing the table as it was (victims' cards still up, the turn
 * panel hidden) while `useAttackAnimation` plays it; `endAttack` � called when the animation
 * is done � reveals the outcome. Attacks that happened before mount aren't replayed.
 */
function useAttackReveal(view: GameView): { view: GameView; attack: AttackView | null; endAttack: () => void } {
  const seq = view.lastAttack?.seq ?? 0;
  const [reveal, setReveal] = useState<{
    seq: number;
    last: GameView;
    frozen: { view: GameView; attack: AttackView } | null;
    /** Attacks that landed while another was still playing; each waits its turn. */
    queue: { view: GameView; attack: AttackView }[];
  }>({ seq, last: view, frozen: null, queue: [] });

  // Work out the new state here and use it for *this* render too: downstream hooks (round end,
  // war) must never see the live view even for one pass, or they would announce the outcome
  // before the animation has played.
  let current = reveal;
  if (seq !== reveal.seq) {
    const before = reveal.last;
    const attack = view.lastAttack;
    const animate = !!attack && seq > reveal.seq && before.gameId === view.gameId && before.phase === 'playing';
    // Show the dice that attacked on the attacker's seat (not what they held before this roll).
    const shownDice = (p: PublicPlayer) =>
      p.id === attack?.attackerId && attack.dice ? { ...p, held: attack.dice } : p;
    const item = animate ? { view: { ...before, turn: null, players: before.players.map(shownDice) }, attack } : null;
    const busy = before.gameId === view.gameId && (reveal.frozen !== null || reveal.queue.length > 0);
    current = busy
      ? { ...reveal, seq, last: view, queue: item ? [...reveal.queue, item] : reveal.queue }
      : { seq, last: view, frozen: item, queue: [] };
    setReveal(current);
  } else if (reveal.last !== view) {
    current = { ...reveal, last: view };
    setReveal(current);
  }

  const endAttack = useCallback(
    () => setReveal((r) => (r.frozen ? { ...r, frozen: r.queue[0] ?? null, queue: r.queue.slice(1) } : r)),
    [],
  );
  return { view: current.frozen?.view ?? view, attack: current.frozen?.attack ?? null, endAttack };
}

/**
 * The dice each player killed with this round, by player, so they stay on the attacker's seat
 * after the attack animation (the server clears their held dice), and who was killed this round
 * (their dice flew off in the attack, so their seats show none). Fed the shown view, so both
 * apply once the animation is done. Cleared each round; attacks before mount aren't shown.
 */
interface KillDice {
  dice: Partial<Record<PlayerId, number[]>>;
  victims: PlayerId[];
}
const NO_KILLS: KillDice = { dice: {}, victims: [] };

function useKillDice(view: GameView): KillDice {
  const attack = view.lastAttack;
  const seq = attack?.seq ?? 0;
  const [state, setState] = useState<{ seq: number; round: number } & KillDice>({
    seq,
    round: view.round,
    ...NO_KILLS,
  });

  let next = state;
  if (view.round !== next.round) next = { ...next, round: view.round, ...NO_KILLS };
  if (seq !== next.seq) {
    next = {
      ...next,
      seq,
      dice: attack?.dice ? { ...next.dice, [attack.attackerId]: attack.dice } : next.dice,
      victims: attack ? [...next.victims, ...attack.victimIds] : next.victims,
    };
  }
  if (next !== state) setState(next);
  return view.phase === 'playing' ? next : NO_KILLS;
}

/** The latest reveal (a war, or play starting) and the cards that were face up before it. */
export interface Reveal {
  key: string;
  faceUp: string[];
}

/** A player's card on the table, so a card chosen after a lost war doesn't count as the one that was face up. */
const upKey = (p: PublicPlayer) => `${p.id}:${p.card}`;
const faceUpCards = (view: GameView) => view.players.filter((p) => p.card !== null && p.revealed).map(upKey);

/**
 * Reveals: each war flips only the cards at war, play starting (after any wars) flips the rest.
 * Remembers which cards were face up before the latest reveal, so the round banner over it can
 * keep the newly revealed ones face down until it closes.
 */
function useReveal(view: GameView): Reveal {
  const revealKey =
    view.phase === 'war' && view.war
      ? `war:${view.round}:${view.war.cardId}`
      : view.phase === 'playing'
        ? `play:${view.round}`
        : '';
  const upNow = faceUpCards(view).join();
  const [state, setState] = useState<{ reveal: Reveal; up: { key: string; ids: string[] } }>({
    reveal: { key: revealKey, faceUp: faceUpCards(view) },
    up: { key: upNow, ids: faceUpCards(view) },
  });
  let next = state;
  if (revealKey && revealKey !== next.reveal.key) next = { ...next, reveal: { key: revealKey, faceUp: next.up.ids } };
  if (upNow !== next.up.key) next = { ...next, up: { key: upNow, ids: faceUpCards(view) } };
  if (next !== state) setState(next);
  return next.reveal;
}

const TARGETED_MS = 3_000;
let targetedSeq = 0;

interface TargetedAlert {
  id: PlayerId;
  name: string;
  /** Your card's Kill Quest, in words. */
  quest: string;
  key: number;
}

/**
 * Beginner mode: when an enemy ends a turn holding dice that improve their odds of rolling your
 * card's Kill Quest, announce it (for `TARGETED_MS`). Moves made before mount aren't announced.
 */
function useTargetedAlert(
  view: GameView,
  catalog: ReadonlyMap<CardId, CardDef>,
  enabled: boolean,
): TargetedAlert | null {
  // Held dice per player as last seen; compared during render (like `useAttackReveal`).
  const heldKey = view.players.map((p) => `${p.id}:${p.held.join()}`).join('|');
  const [seen, setSeen] = useState<{ key: string; view: GameView; alert: TargetedAlert | null }>({
    key: heldKey,
    view,
    alert: null,
  });
  let current = seen;
  if (heldKey !== seen.key) {
    const self = view.you.spectating ? undefined : view.players.find((p) => p.id === view.you.id);
    const mine = self && self.card !== null ? catalog.get(self.card) : undefined;
    let alert = seen.alert;
    if (enabled && mine && view.phase === 'playing') {
      for (const p of view.players) {
        const before = seen.view.players.find((q) => q.id === p.id)?.held.join();
        if (p.id === view.you.id || p.card === null || before === p.held.join()) continue;
        const power = catalog.get(p.card)?.power ?? null;
        if (heldTargets(mine.killer, power, p.held)) {
          alert = { id: p.id, name: p.name, quest: killerName(mine), key: ++targetedSeq };
        }
      }
    }
    current = { key: heldKey, view, alert };
    setSeen(current);
  }
  const alert = current.alert;
  useEffect(() => {
    if (!alert) return;
    const t = setTimeout(() => setSeen((s) => (s.alert === alert ? { ...s, alert: null } : s)), TARGETED_MS);
    return () => clearTimeout(t);
  }, [alert]);
  return alert;
}

const NO_DICE: readonly number[] = [];
const NO_MODS: readonly DieMod[] = [];

/**
 * Whether a bot's announced move (`key`, null for none) shows its dice locked yet: at once,
 * unless it changes dice with a power (`steps`, see `usePlanSteps`), then `PLAN_LOCK_MS` after
 * the change's last step shows.
 */
function usePlanLock(key: string | null, steps: number): boolean {
  const [locked, setLocked] = useState<string | null>(key);
  useEffect(() => {
    if (key === null || steps === 0) return;
    const timer = setTimeout(() => setLocked(key), anim((steps - 1) * PLAN_STEP_MS + PLAN_LOCK_MS));
    return () => clearTimeout(timer);
  }, [key, steps]);
  return key !== null && (steps === 0 || locked === key);
}

/**
 * The faces a changed die shows on its way: just the new one, or with the dice wild every face
 * in between, counting up or down (never round from 6 to 1 or back).
 */
function changePath(from: number, to: number, wild: boolean): number[] {
  if (!wild || from === to) return [to];
  const dir = to > from ? 1 : -1;
  const out: number[] = [];
  for (let v = from + dir; v !== to + dir; v += dir) out.push(v);
  return out;
}

/** A bot's dice change as it plays out (see `usePlanSteps`). */
interface PlanSteps {
  /** Steps the whole change takes, every changed die in turn (0: no change). */
  steps: number;
  /** The face die `i` shows now (undefined: not changed). */
  value: (i: number) => number | undefined;
  /** The arrow flashing on die `i` now, with the step (so each step flashes anew). */
  flash: (i: number) => { dir: 'up' | 'down'; step: number } | undefined;
  /** Changed dice not set yet: they still burn. */
  burning: readonly number[];
}

/**
 * A bot's announced move (`key`) that changes dice: the changed dice move one at a time, left to
 * right. Each step (`PLAN_STEP_MS`) the moving die flashes an arrow (up or down) and turns to its
 * next face (`changePath`); once on its new face it is set, its flame goes out and the next die
 * starts. Dice still to move show their rolled face. The total matches `planChangeSteps`.
 */
function usePlanSteps(key: string | null, pool: readonly number[], mods: readonly DieMod[], wild: boolean): PlanSteps {
  const [state, setState] = useState<{ key: string | null; step: number }>({ key, step: 1 });
  let current = state;
  if (state.key !== key) {
    current = { key, step: 1 };
    setState(current);
  }
  const step = current.step;
  // Each changed die with its path and the steps before it starts.
  const paths = new Map<number, { from: number; path: number[]; start: number }>();
  let steps = 0;
  if (key !== null) {
    for (const m of [...mods].sort((a, b) => a.index - b.index)) {
      const path = changePath(pool[m.index]!, m.value, wild);
      paths.set(m.index, { from: pool[m.index]!, path, start: steps });
      steps += path.length;
    }
  }
  useEffect(() => {
    if (key === null || step > steps) return;
    const timer = setTimeout(
      () => setState((s) => (s.key === key ? { key, step: s.step + 1 } : s)),
      anim(PLAN_STEP_MS),
    );
    return () => clearTimeout(timer);
  }, [key, step, steps]);
  /** How far die `i`'s own path has got (0: not started). */
  const at = (p: { path: number[]; start: number }) => Math.min(Math.max(step - p.start, 0), p.path.length);
  return {
    steps,
    value: (i) => {
      const p = paths.get(i);
      if (!p) return undefined;
      const n = at(p);
      return n === 0 ? p.from : p.path[n - 1];
    },
    flash: (i) => {
      const p = paths.get(i);
      const n = p ? step - p.start : 0;
      if (!p || n < 1 || n > p.path.length) return undefined;
      const prev = n === 1 ? p.from : p.path[n - 2]!;
      return { dir: p.path[n - 1]! > prev ? 'up' : 'down', step };
    },
    burning: [...paths].filter(([, p]) => step <= p.start + p.path.length).map(([i]) => i),
  };
}

/** Someone else's attack: their attacking dice show this long before the animation starts. */
const ATTACK_HOLD_MS = 1_000;
/** The seats gliding between layouts (and the dealer chip sliding seat to seat). */
const CARD_FLY_MS = 400;
/** An attack waiting for the table to settle starts after this anyway (nothing can hold it up for good). */
const ATTACK_SETTLE_MAX_MS = 6_000;

/**
 * Plays the attack on the table's cards, and calls `onDone` when it has finished (or can't play).
 * The attacker stays in the big seat throughout (`useTurnHandoff`'s hold), so the attacker's card
 * leaves from there; it shrinks back to its seat once the animation is done. Portrait Layout, when
 * the turn has moved on (the attacker leaves the big seat after this): an animation that goes
 * offscreen brings the card back at its seat's size and lands it in its seat (`landedRef` names it,
 * so the seat doesn't glide back from the big seat). Someone else's
 * attack waits `ATTACK_HOLD_MS` first, so the dice that made it are seen; yours waits a little
 * over `CARD_FLY_MS`, while your controls give way to the dice that attacked.
 *
 * Nothing starts until `settled` (the attacker has the big seat, no seat is gliding, no roll is
 * playing): an attack queued behind another (a bot that killed right after you) waits for the
 * last player to get back to their place and the attacker to grow into the big seat, then a
 * further `CARD_FLY_MS` for that grow, then its hold. At most `ATTACK_SETTLE_MAX_MS` of waiting.
 */
function useAttackAnimation(
  table: React.RefObject<HTMLElement | null>,
  attack: AttackView | null,
  settled: boolean,
  onDone: () => void,
  me: PlayerId,
  shown: GameView,
  /** The latest view: whose turn it is now the attack has landed. */
  live: GameView,
  landedRef: React.MutableRefObject<PlayerId | null>,
  /** Haptics on (and playing, not watching): buzz when a kill by you or on you lands. */
  haptics: boolean,
) {
  // Read inside the effect without restarting it when the view changes.
  const shownRef = useRef(shown);
  const liveRef = useRef(live);
  useEffect(() => {
    shownRef.current = shown;
    liveRef.current = live;
  });
  // The attack cleared to play: once the table has settled, after the hold (and the grow, if it had to wait).
  const [go, setGo] = useState<AttackView | null>(null);
  const waited = useRef<{ attack: AttackView | null; unsettled: boolean }>({ attack: null, unsettled: false });
  useEffect(() => {
    if (!attack || go === attack) return;
    if (waited.current.attack !== attack) waited.current = { attack, unsettled: !settled };
    if (!settled) {
      const fallback = setTimeout(() => setGo(attack), ATTACK_SETTLE_MAX_MS);
      return () => clearTimeout(fallback);
    }
    const hold = attack.attackerId === me ? CARD_FLY_MS + 100 : anim(ATTACK_HOLD_MS);
    const timer = setTimeout(() => setGo(attack), hold + (waited.current.unsettled ? CARD_FLY_MS : 0));
    return () => clearTimeout(timer);
  }, [attack, settled, go, me]);
  const cleared = attack !== null && go === attack ? attack : null;
  useLayoutEffect(() => {
    const attack = cleared;
    if (!attack || !table.current) return;
    const el = table.current;
    let playing: ReturnType<typeof playAttack> = null;
    let done: ReturnType<typeof setTimeout> | undefined;
    let nice: ReturnType<typeof setTimeout> | undefined;
    const impact = () => {
      if (haptics) buzzAttack(attack, me);
    };
    const start = () => {
      const now = liveRef.current;
      const leaving = now.phase === 'playing' && now.turn?.playerId !== attack.attackerId;
      playing = playAttack(el, attack, impact, leaving);
      landedRef.current = playing?.home ? attack.attackerId : null;
      if (!playing) {
        impact();
        return onDone();
      }
      done = setTimeout(() => onDone(), playing.duration + 700);
      // Sounds: a bot killing a player laughs at them; you killing more than one cards earns praise.
      const players = shownRef.current.players;
      const bot = !!players.find((p) => p.id === attack.attackerId)?.bot;
      if (bot && attack.victimIds.some((id) => !players.find((p) => p.id === id)?.bot)) {
        playSound(botKillSound());
      } else if (attack.attackerId === me && attack.victimIds.length > 1) {
        nice = setTimeout(() => playSound('niceJob'), playing.duration);
      }
    };
    start();
    return () => {
      clearTimeout(done);
      clearTimeout(nice);
      playing?.cancel();
    };
  }, [cleared, table, onDone, me, landedRef, haptics]);
}

/** Animate `el` from where `from` was to where it is now (moving and scaling, as one card to another). */
function flyFrom(el: Element | null | undefined, from: DOMRect | null | undefined) {
  if (!el || !from || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const to = el.getBoundingClientRect();
  if (to.width === 0 || from.width === 0) return;
  const move = `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width})`;
  el.animate([{ transform: move }, { transform: 'none' }], { duration: CARD_FLY_MS, easing: 'ease-in-out' });
}

/**
 * Who has the big seat: whoever plays a turn, null between turns. When the turn passes the big
 * seat empties at once (that player shrinks back while everyone else glides to full size in seat
 * order), and the moment that glide ends (`CARD_FLY_MS`) the next player grows into it, with no pause. No big seat
 * in a ghost roll or while play is paused for a Beginner screen. (The dealer chip only moves
 * between rounds: see `useChipFly`.) `hold`: an attack is playing; its attacker keeps (or gets)
 * the big seat until it is done, whatever the turn or a pause says.
 *
 * `stepBack` (Layout v1): the turn's player first shrinks back into their spot in the slug row
 * on its own (`back`: who, for `CARD_FLY_MS`; the slugs never move), then the next player grows
 * straight out of theirs. Only with no next player (a ghost roll, a pause, the round over) does
 * everyone glide to full size. `returned`: who just stepped back, for `CARD_FLY_MS` after; their
 * slug fades in.
 *
 * `makeRoom` (Portrait Layout on phones): the column first opens a slug-sized gap where the turn's
 * player sits (`room`: who, for `CARD_FLY_MS`; they keep the big seat meanwhile), then their seat
 * glides into it, then the next player grows. Not when an attack already landed their card home
 * (`landed`): their seat appears in place at once, as before.
 */
interface Handoff {
  big: PlayerId | null;
  back: PlayerId | null;
  room: PlayerId | null;
  returned: PlayerId | null;
}

function useTurnHandoff(
  view: GameView,
  paused: boolean,
  hold: PlayerId | null,
  stepBack: boolean,
  makeRoom: boolean,
  landed: React.MutableRefObject<PlayerId | null>,
): Handoff {
  const turn = view.phase === 'playing' ? view.turn : null;
  const wanted =
    view.phase === 'playing' && hold !== null ? hold : turn && !turn.ghost && !paused ? turn.playerId : null;
  const [state, setState] = useState<Handoff>({ big: wanted, back: null, room: null, returned: null });
  // The turn moved on (or paused): the big seat empties in this very render.
  let current = state;
  if (state.big !== null && state.big !== wanted) {
    const room = makeRoom && landed.current !== state.big ? state.big : null;
    current = { big: null, back: stepBack ? state.big : null, room, returned: null };
    setState(current);
  }
  const { big, back, room, returned } = current;
  // Read when the step back ends: whoever should have the big seat by then.
  const wantedRef = useRef(wanted);
  useEffect(() => {
    wantedRef.current = wanted;
  });
  useEffect(() => {
    if (back === null) return;
    const timer = setTimeout(
      () => setState((s) => (s.back === back ? { big: wantedRef.current, back: null, room: null, returned: back } : s)),
      CARD_FLY_MS,
    );
    return () => clearTimeout(timer);
  }, [back]);
  // The gap is open: the seat glides into it.
  useEffect(() => {
    if (room === null) return;
    const timer = setTimeout(() => setState((s) => (s.room === room ? { ...s, room: null } : s)), CARD_FLY_MS);
    return () => clearTimeout(timer);
  }, [room]);
  useEffect(() => {
    if (big !== null || back !== null || room !== null || wanted === null) return;
    const timer = setTimeout(() => setState({ big: wanted, back: null, room: null, returned: null }), CARD_FLY_MS);
    return () => clearTimeout(timer);
  }, [big, back, room, wanted]);
  useEffect(() => {
    if (returned === null) return;
    const timer = setTimeout(
      () => setState((s) => (s.returned === returned ? { ...s, returned: null } : s)),
      CARD_FLY_MS,
    );
    return () => clearTimeout(timer);
  }, [returned]);
  return current;
}

/**
 * Layout v1, a turn over: the big seat (`back`'s, kept as `leaving`) flies into their spot in the
 * slug row over `CARD_FLY_MS`. The whole card shrinks on the way until it fits the spot's window
 * (reached by `FIT_AT`, just outside the window), then keeps heading for the window's centre and
 * shrinks to nothing as it gets there, so it never touches a wall it heads for. Everything else
 * on the seat fades out (CSS, `.seat.big.leaving`). The slug then fades in (`useTurnHandoff`
 * `returned`). The slugs never move.
 */
const FIT_AT = 0.7;

function useShrinkToSpot(table: React.RefObject<HTMLElement | null>, back: PlayerId | null) {
  useLayoutEffect(() => {
    const el = table.current;
    if (back === null || !el) return;
    const seat = el.querySelector<HTMLElement>(':scope > .big-wrap > .seat.leaving');
    const win = el.querySelector<HTMLElement>(`.seat.spot[data-seat-id="${back}"] :is(.strip-window, .spot-card)`);
    if (!seat || !win) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      seat.style.visibility = 'hidden';
      return;
    }
    const s = seat.getBoundingClientRect();
    const w = win.getBoundingClientRect();
    const r = seat.querySelector<HTMLElement>('.card-face > .card')?.getBoundingClientRect();
    if (!r || r.width === 0 || s.width === 0) return;
    const from = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    const to = { x: w.left + w.width / 2, y: w.top + w.height / 2 };
    const fit = Math.min(w.width / r.width, w.height / r.height);
    // Heading for the window's centre (straight down if already over it).
    const len = Math.hypot(to.x - from.x, to.y - from.y);
    const ux = len > 0 ? (to.x - from.x) / len : 0;
    const uy = len > 0 ? (to.y - from.y) / len : 1;
    // Fitted, the card sits just outside the window's near wall: centre-to-wall along the heading
    // plus the fitted card's reach along it. From there, centre and size close on zero together,
    // so its leading edge stays behind the centre and never meets the far wall.
    const wall = Math.min(ux ? w.width / 2 / Math.abs(ux) : Infinity, uy ? w.height / 2 / Math.abs(uy) : Infinity);
    const reach = (fit * (r.width * Math.abs(ux) + r.height * Math.abs(uy))) / 2;
    const back0 = Math.min(wall + reach, len);
    const at = { x: to.x - ux * back0, y: to.y - uy * back0 };
    // Seat transforms from its top-left corner: move the card's centre to `p` at scale `k`.
    const pose = (p: { x: number; y: number }, k: number) =>
      `translate(${p.x - s.left - k * (from.x - s.left)}px, ${p.y - s.top - k * (from.y - s.top)}px) scale(${k})`;
    const move = seat.animate(
      [
        { transform: 'none', transformOrigin: 'top left', easing: 'ease-in' },
        { transform: pose(at, fit), transformOrigin: 'top left', offset: FIT_AT, easing: 'ease-out' },
        { transform: pose(to, 0), transformOrigin: 'top left' },
      ],
      { duration: CARD_FLY_MS, fill: 'forwards' },
    );
    return () => move.cancel();
  }, [table, back]);
}

/**
 * The dealer chip slides from its last seat to its new one. It moves only between rounds: the
 * shown view keeps the old dealer under the Round Over banner, so it slides as the banner closes.
 */
function useChipFly(table: React.RefObject<HTMLElement | null>, chip: PlayerId | null) {
  const last = useRef<DOMRect | null>(null);
  const was = useRef(chip);
  useLayoutEffect(() => {
    const el = table.current?.querySelector<HTMLElement>('.dealer-chip');
    if (was.current !== chip) {
      flyFrom(el, last.current);
      was.current = chip;
    }
    const rect = el?.getBoundingClientRect();
    if (rect) last.current = rect;
  });
}

/**
 * When the layout changes (a turn's big seat comes or goes, or Portrait Layout's column comes or
 * goes; `layout` names both), the seats re-flow: rows, sizes and order change. Instead of popping into place, every seat glides and
 * scales from where it was (matched by `data-seat-id`, so a seat that moved to another row, or
 * into the big seat, still glides). `landedRef`: an attacker whose card already flew into its seat
 * (`useAttackAnimation`); on the next change its seat appears in place, and `landedRef` is cleared.
 */
function useSeatFlip(
  table: React.RefObject<HTMLElement | null>,
  layout: string,
  landedRef: React.MutableRefObject<PlayerId | null>,
) {
  const last = useRef(new Map<string, DOMRect>());
  const from = useRef<Map<string, DOMRect> | null>(null);
  const skip = useRef<string | null>(null);
  const was = useRef(layout);
  const running = useRef<Animation[]>([]);
  useLayoutEffect(() => {
    const el = table.current;
    if (!el) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (was.current !== layout) {
      // Where the seats were last seen (mid-glide, if one was still running).
      from.current = new Map(last.current);
      was.current = layout;
      skip.current = landedRef.current === null ? null : String(landedRef.current);
      landedRef.current = null;
      // The fits settle over a render or two in this frame: start from the old places until the next frame.
      requestAnimationFrame(() => {
        from.current = null;
        skip.current = null;
      });
    }
    // Back to the laid-out places before measuring (restarted on each render of the change).
    if (from.current) {
      running.current.forEach((a) => a.cancel());
      running.current = [];
    }
    const now = new Map<string, DOMRect>();
    for (const seat of el.querySelectorAll<HTMLElement>('.seat[data-seat-id]')) {
      // Portrait Layout's name-only place in the column is matched apart from the seat itself.
      const id = seat.dataset.flip ? `${seat.dataset.seatId}:${seat.dataset.flip}` : seat.dataset.seatId!;
      const rect = seat.getBoundingClientRect();
      now.set(id, rect);
      const old = from.current?.get(id);
      if (!old || reduced || rect.width === 0 || id === skip.current) continue;
      const dx = old.left - rect.left;
      const dy = old.top - rect.top;
      const k = old.width / rect.width;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1 && Math.abs(k - 1) < 0.01) continue;
      const origin = 'top left';
      running.current.push(
        seat.animate(
          [
            { transform: `translate(${dx}px, ${dy}px) scale(${k})`, transformOrigin: origin },
            { transform: 'none', transformOrigin: origin },
          ],
          { duration: CARD_FLY_MS, easing: 'ease-in-out' },
        ),
      );
    }
    last.current = now;
  });
}

/** True for `CARD_FLY_MS` after the layout changes (`layout`, as `useSeatFlip`'s): the seats are gliding. */
function useCardInFlight(layout: string): boolean {
  const [state, setState] = useState({ layout, flying: false });
  let current = state;
  if (state.layout !== layout) {
    current = { layout, flying: true };
    setState(current);
  }
  useEffect(() => {
    if (!state.flying) return;
    const t = setTimeout(() => setState((s) => (s === state ? { ...s, flying: false } : s)), CARD_FLY_MS);
    return () => clearTimeout(t);
  }, [state]);
  return current.flying;
}

/** Seats per row for `count` seats in `rows` rows, front rows fullest (5 in 2: 3 + 2). */
function splitRows<T>(items: T[], rows: number): T[][] {
  const per = Math.ceil(items.length / Math.max(1, rows));
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += per) out.push(items.slice(i, i + per));
  return out.length > 0 ? out : [[]];
}

/** Largest seat card in a game, in px (the art is 370 wide). */
const FIT_MAX_CARD = 300;
const FIT_MIN_CARD = 30;
/** Room kept under every seat row in a war for the war dice: card widths + rem (mirrors `.opponents.war`). */
const WAR_ROOM_PER_CARD = 0.2;
const WAR_ROOM_REM = 1.2;

/**
 * The bottom of the room the game may fill: the content area's, or on phones (where the content
 * row is only as tall as the game, the chat taking the rest) the screen less the chat's minimum
 * (the sidebar's min-height).
 */
function roomBottom(scroller: HTMLElement, side: HTMLElement | null): number {
  const view = scroller.getBoundingClientRect();
  const padBottom = parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
  const chatMin = side ? parseFloat(getComputedStyle(side).minHeight) || 0 : 0;
  const floor = side && chatMin > 0 ? side.getBoundingClientRect().bottom - chatMin : view.bottom;
  return Math.min(floor, window.innerHeight) - padBottom;
}

/**
 * Desktop, Layout v1 in a turn: the player's seat in the row has a card this many times the
 * others', and beside it a panel (name, power, buttons, dice) this many card widths wide.
 * Keep in step with `.opponents .seat.big` in the CSS.
 */
const IN_ROW_SCALE = 1.5;
const IN_ROW_PANEL = 1.7;

/**
 * In a game, between turns: the seats as large as they fit on one screen above the middle panel.
 * Desktop, Layout v1 in a turn (`inRow`): as large as fit the whole room, one of them the turn's
 * player's seat, `IN_ROW_SCALE` times as big with its `IN_ROW_PANEL` beside the card.
 * Measures one seat to split its size into a fixed part (name, padding, the hand peeking out,
 * the dice row, which never changes size) and the card, which grows, then tries every number of
 * rows and keeps the one giving the biggest card. Re-measured whenever the screen or the table
 * changes size. Null when `count` is 0 (the lobby, or a turn: see `useBigFit`).
 */
function useSeatFit(table: React.RefObject<HTMLElement | null>, count: number, war: boolean, inRow: boolean) {
  const [fit, setFit] = useState<{ rows: number; card: number } | null>(null);
  useLayoutEffect(() => {
    const el = table.current;
    if (!el || count === 0) return;
    const scroller = el.closest<HTMLElement>('.content') ?? document.documentElement;
    const side = document.querySelector<HTMLElement>('.shell > .sidebar');
    const measure = () => {
      const rows = [...el.querySelectorAll<HTMLElement>(':scope > .opponents')];
      // A full seat, not the turn's greyed spot or big seat.
      const seat = el.querySelector<HTMLElement>(':scope > .opponents > .seat:not(.spot):not(.big)');
      const card = seat?.querySelector<HTMLElement>('.card-face .card');
      const stack = seat?.querySelector<HTMLElement>('.card-stack');
      const dice = seat?.querySelector<HTMLElement>('.dice.small');
      const anchor = el.querySelector<HTMLElement>(':scope > .table-center');
      if (!seat || !card || !stack || !dice || (!anchor && !inRow)) return;
      // Layout sizes (offset*), not getBoundingClientRect: a card mid-flight or mid-attack is
      // transformed (scaled), which would make the seats look huge and pick a single row.
      const w0 = card.offsetWidth;
      if (w0 === 0) return;
      // Seat width: its padding plus the wider of the card stack (card + hand peeking out) and the dice.
      const stackW = stack.offsetWidth;
      const diceW = dice.offsetWidth;
      const padW = seat.offsetWidth - Math.max(stackW, diceW);
      const peekW = stackW - w0;
      const fixedH = seat.offsetHeight - (w0 * 64) / 37;
      const perW = 64 / 37;
      // Room for the seat rows: what they take now plus what is left under the middle panel.
      const top = rows[0]!.getBoundingClientRect().top;
      const bottom = rows[rows.length - 1]!.getBoundingClientRect().bottom;
      // As if scrolled to the top, so the answer does not depend on where you are scrolled to.
      const tableStyle = getComputedStyle(el);
      const rowGap = parseFloat(tableStyle.rowGap) || 0;
      const colGap = parseFloat(getComputedStyle(rows[0]!).columnGap) || 0;
      const roomH = inRow
        ? roomBottom(scroller, side) - (top + scroller.scrollTop)
        : bottom - top + roomBottom(scroller, side) - (anchor!.getBoundingClientRect().bottom + scroller.scrollTop + 8);
      const roomW = rows[0]!.clientWidth;
      // A war: each row also keeps room under it for the big war dice (see `.opponents.war` in the CSS).
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const warH = war ? WAR_ROOM_REM * rem : 0;
      const warPerW = war ? WAR_ROOM_PER_CARD : 0;
      let best = { rows: 1, card: 0 };
      for (let r = 1; r <= count; r++) {
        const cols = Math.ceil(count / r);
        // (1px spare a seat: seats a fraction of a pixel too wide wrap to a row of their own.)
        const byW = inRow
          ? (roomW - (cols - 1) * colGap - cols * (padW + peekW + 1) - rem) / (cols - 1 + IN_ROW_SCALE + IN_ROW_PANEL)
          : (roomW - (cols - 1) * colGap) / cols - padW - peekW - 1;
        const byH = inRow
          ? (roomH - (r - 1) * rowGap - r * fixedH) / ((r - 1 + IN_ROW_SCALE) * perW)
          : (roomH - (r - 1) * rowGap - r * (fixedH + warH)) / (r * (perW + warPerW));
        const w = Math.min(byW, byH, FIT_MAX_CARD);
        if (w > best.card + 1) best = { rows: r, card: w };
      }
      const next = { rows: best.rows, card: Math.floor(Math.max(FIT_MIN_CARD, best.card)) };
      setFit((f) => (f && f.rows === next.rows && Math.abs(f.card - next.card) <= 1 ? f : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scroller);
    ro.observe(el);
    if (side) ro.observe(side);
    return () => ro.disconnect();
  }, [table, count, war, inRow]);
  return count === 0 ? null : fit;
}

/** The big seat's card, in px. */
const BIG_MAX_CARD = 260;
/** Portrait Layout: the big seat has its column to itself, so its card may grow larger. */
const SIDE_MAX_CARD = 420;
const BIG_MIN_CARD = 60;

/**
 * A turn: the big seat's card as large as fits with the whole table on one screen (the cropped
 * seats above it, its dice, and on your turn your buttons and hints: nothing to scroll), up to
 * `BIG_MAX_CARD`, and at most 60% of the width (the name, power and buttons take the rest). Its
 * height grows 64/37 of its width, so one measurement gives the answer.
 * Re-measured whenever the screen or the table changes size. Null when `on` is false.
 */
function useBigFit(
  table: React.RefObject<HTMLElement | null>,
  on: boolean,
  /** Portrait Layout: the big seat is stacked in a stage of its own (card, then dice and buttons under it). */
  sideLayout: boolean,
  /** Portrait Layout: the column's width (re-measured when it changes). */
  listW: number | null,
): number | null {
  const [card, setCard] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = table.current;
    if (!el || !on) return;
    const scroller = el.closest<HTMLElement>('.content') ?? document.documentElement;
    const side = document.querySelector<HTMLElement>('.shell > .sidebar');
    const measure = () => {
      const wrap = el.querySelector<HTMLElement>(sideLayout ? '.side-stage > .big-wrap' : ':scope > .big-wrap');
      const seat = wrap?.querySelector<HTMLElement>('.seat.big');
      const img = seat?.querySelector<HTMLElement>('.card-face .card');
      const stack = seat?.querySelector<HTMLElement>('.card-stack');
      if (!wrap || !seat || !img || !stack) return;
      const w0 = img.offsetWidth;
      if (w0 === 0) return;
      // The table's own bottom, as if scrolled to the top: everything on it must fit. Portrait
      // Layout: the big seat's own bottom (the column beside it may be the taller one) plus the
      // table's padding under it.
      const bottom = sideLayout
        ? wrap.getBoundingClientRect().bottom + (parseFloat(getComputedStyle(el).paddingBottom) || 0)
        : el.getBoundingClientRect().bottom;
      const slack = roomBottom(scroller, side) - (bottom + scroller.scrollTop + 4);
      const byH = w0 + (slack * 37) / 64;
      // At most 60% of the width less the gap: the name, power and buttons keep at least 40%.
      // Portrait Layout: the big seat's whole column, less the deck peeking out.
      const gap = parseFloat(getComputedStyle(seat).columnGap) || 0;
      const peek = stack.offsetWidth - w0;
      const byW = sideLayout ? seat.clientWidth - peek : seat.clientWidth * 0.6 - gap - peek;
      const max = sideLayout ? SIDE_MAX_CARD : BIG_MAX_CARD;
      const next = Math.floor(Math.min(max, Math.max(BIG_MIN_CARD, Math.min(byH, byW))));
      setCard((c) => (c !== null && Math.abs(c - next) <= 1 ? c : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scroller);
    ro.observe(el);
    if (side) ro.observe(side);
    return () => ro.disconnect();
  }, [table, on, sideLayout, listW]);
  return on ? card : null;
}

/** Portrait Layout's column: at most this share of the table's width, and never narrower than `LIST_MIN_W`. */
const LIST_MAX_SHARE = 0.4;
const LIST_MIN_W = 56;
/** Desktop: the column of full seats (a grid) may take up to this share of the table's width. */
const GRID_MAX_SHARE = 0.5;

/**
 * Portrait Layout: the column's width (px), so that `count` cropped seats stacked in it fit on one
 * screen, up to `LIST_MAX_SHARE` of the table's width. Measures one seat to split its height into
 * a fixed part (name, padding) and the parts that grow with its width (the power window and
 * the dice row, both sized off the seat's width). Null when `count` is 0 (not in Portrait play).
 * Desktop (`roomy`): full seats in a grid instead; tries every number of columns (up to
 * `GRID_MAX_SHARE` of the width) and keeps the biggest card, returned as `card` with the width.
 */
function useListFit(
  table: React.RefObject<HTMLElement | null>,
  count: number,
  roomy: boolean,
): { width: number; card: number | null } | null {
  const [fit, setFit] = useState<{ width: number; card: number | null } | null>(null);
  useLayoutEffect(() => {
    const el = table.current;
    if (!el || count === 0) return;
    const scroller = el.closest<HTMLElement>('.content') ?? document.documentElement;
    const side = document.querySelector<HTMLElement>('.shell > .sidebar');
    const keep = (next: { width: number; card: number | null }) =>
      setFit((f) =>
        f !== null && Math.abs(f.width - next.width) <= 1 && Math.abs((f.card ?? 0) - (next.card ?? 0)) <= 1 ? f : next,
      );
    const measure = () => {
      const list = el.querySelector<HTMLElement>('.side-list');
      const layout = list?.parentElement;
      if (!list || !layout) return;
      const gap = parseFloat(getComputedStyle(list).rowGap) || 0;
      const padBottom = parseFloat(getComputedStyle(el).paddingBottom) || 0;
      // As if scrolled to the top, so the answer does not depend on where you are scrolled to.
      const top = list.getBoundingClientRect().top + scroller.scrollTop;
      const roomH = roomBottom(scroller, side) - top - padBottom - 4;
      if (roomy) {
        // A full seat (not the big seat's spot): its fixed part and its card, as in `useSeatFit`.
        const seat = list.querySelector<HTMLElement>('.seat:not(.spot)');
        const card = seat?.querySelector<HTMLElement>('.card-face .card');
        const stack = seat?.querySelector<HTMLElement>('.card-stack');
        const dice = seat?.querySelector<HTMLElement>('.dice.small');
        if (!seat || !card || !stack || !dice) return;
        const w0 = card.offsetWidth;
        if (w0 === 0) return;
        const padW = seat.offsetWidth - Math.max(stack.offsetWidth, dice.offsetWidth);
        const peekW = stack.offsetWidth - w0;
        const fixedH = seat.offsetHeight - (w0 * 64) / 37;
        const maxW = layout.clientWidth * GRID_MAX_SHARE;
        let best = { cols: 1, card: 0 };
        for (let c = 1; c <= count; c++) {
          const r = Math.ceil(count / c);
          const byW = (maxW - (c - 1) * gap) / c - padW - peekW - 1;
          const byH = (roomH - (r - 1) * gap - r * fixedH) / ((r * 64) / 37);
          const w = Math.min(byW, byH, FIT_MAX_CARD);
          if (w > best.card + 1) best = { cols: c, card: w };
        }
        const cardW = Math.floor(Math.max(FIT_MIN_CARD, best.card));
        keep({ width: Math.ceil(best.cols * (cardW + padW + peekW + 1) + (best.cols - 1) * gap), card: cardW });
        return;
      }
      // A full seat, not the big seat's name-only place.
      const seat = list.querySelector<HTMLElement>('.seat.strip');
      const win = seat?.querySelector<HTMLElement>('.strip-window');
      const dice = seat?.querySelector<HTMLElement>('.dice.small');
      if (!seat || !win || !dice) return;
      // Layout sizes (offset*): a seat mid-glide is transformed.
      const w0 = seat.offsetWidth;
      if (w0 === 0) return;
      const grows = win.offsetHeight + dice.offsetHeight;
      const fixed = seat.offsetHeight - grows;
      const perW = grows / w0;
      const byH = (roomH - (count - 1) * gap - count * fixed) / (count * perW);
      const byW = layout.clientWidth * LIST_MAX_SHARE;
      keep({ width: Math.floor(Math.max(LIST_MIN_W, Math.min(byH, byW))), card: null });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scroller);
    ro.observe(el);
    if (side) ro.observe(side);
    return () => ro.disconnect();
  }, [table, count, roomy]);
  return count === 0 ? null : fit;
}

/**
 * When a war round settles, keep showing the table as it was — with everyone's final rolls —
 * for a moment after the last roll's animation, so it is seen before cards die; then, when
 * someone lost, the winner attacks the losers' cards (with a tie at the top, the first of the
 * tied). Wars settled before mount aren't replayed.
 */
function useWarReveal(view: GameView, table: React.RefObject<HTMLElement | null>): { view: GameView; busy: boolean } {
  const key = (w: GameView['lastWar']) => (w ? `${w.cardId}:${w.round}:${JSON.stringify(w.rolls)}` : '');
  const settled = key(view.lastWar);
  // `roll`: the last war roll and its time on this screen (the 3D tray can hold it open longer).
  const [reveal, setReveal] = useState<{
    settled: string;
    last: GameView;
    frozen: GameView | null;
    ms: number;
    roll: { seq: number | null; ms: number };
  }>({
    settled,
    last: view,
    frozen: null,
    ms: 0,
    roll: { seq: null, ms: 0 },
  });

  // The new state is used for *this* render too (as in `useAttackReveal`): downstream hooks must
  // never see the live view, or the Beginner intros would start before the war's attack plays.
  let current = reveal;
  if (settled !== reveal.settled) {
    // A war round just settled: freeze the table as it was, plus the final rolls.
    const before = reveal.last;
    const war = view.lastRoll?.war ? view.lastRoll : null;
    current = {
      settled,
      last: view,
      frozen: before.phase === 'war' ? { ...before, war: view.lastWar } : null,
      ms: anim(WAR_REVEAL_MS),
      roll: { seq: war?.seq ?? null, ms: war ? rollMs(war) : 0 },
    };
    setReveal(current);
  } else if (reveal.last !== view) {
    current = { ...reveal, last: view };
    setReveal(current);
  }

  const { frozen, ms, roll } = current;
  useEffect(() => {
    if (!frozen) return;
    const unfreeze = () => setReveal((r) => ({ ...r, frozen: null }));
    let playing: ReturnType<typeof playAttack> = null;
    let done: ReturnType<typeof setTimeout> | undefined;
    const attack = () => {
      const war = frozen.war;
      const total = (id: PlayerId) => (war?.rolls[id] ?? []).reduce((a, b) => a + b, 0);
      const ids = war?.participants ?? [];
      const best = Math.max(...ids.map(total));
      const victimIds = ids.filter((id) => total(id) < best);
      const attackerId = ids.find((id) => total(id) === best);
      playing =
        table.current && attackerId !== undefined && victimIds.length > 0
          ? playAttack(table.current, { seq: 0, attackerId, victimIds })
          : null;
      if (!playing) return unfreeze();
      done = setTimeout(unfreeze, playing.duration + 700);
    };
    const cancel = afterRoll(roll.seq, roll.ms, ms, attack);
    return () => {
      cancel();
      clearTimeout(done);
      playing?.cancel();
    };
  }, [frozen, ms, roll, table]);

  return { view: frozen ?? view, busy: frozen !== null };
}

interface RoundEnd {
  /** The round about to start, or null when the game ended. */
  next: number | null;
  /** The next round's dealer, when play starts straight away (no choosing or war first). */
  dealerId: PlayerId | null;
  frozen: GameView;
}

/**
 * When a round of turns ends, hold the table for a moment — the outcome (dead cards) shown,
 * but no turn, no card choosing, round and pass unchanged — under the 'Round Over' banner.
 * Further updates during the hold are kept and shown once it ends. Rounds that ended before
 * mount aren't replayed. The banner stays until Next; a game that ended has no banner, so its
 * final table holds for `GAME_END_HOLD_MS`, then game over shows.
 */
const GAME_END_HOLD_MS = 2_000;

function useRoundEnd(view: GameView): { view: GameView; roundEnd: RoundEnd | null; clearRoundEnd: () => void } {
  const [state, setState] = useState<{ last: GameView; end: RoundEnd | null }>({ last: view, end: null });

  // Used for this render too (see `useAttackReveal`), so nothing downstream sees the live view.
  let current = state;
  if (state.last !== view) {
    const before = state.last;
    const ended = before.phase === 'playing' && (view.phase !== 'playing' || view.round !== before.round);
    const end: RoundEnd | null = ended
      ? {
          // Learn to Play ends after round 1: its Round Over banner shows as for any round.
          next: view.phase === 'finished' && !view.learn ? null : before.round + 1,
          dealerId: view.phase === 'playing' ? view.dealerId : null,
          frozen: {
            ...view,
            players: view.players.map((p) => {
              const was = before.players.find((b) => b.id === p.id);
              return p.card === null && was ? { ...p, status: was.status } : p;
            }),
            phase: 'playing',
            round: before.round,
            sequence: before.sequence,
            dealerId: before.dealerId,
            turn: null,
            winnerId: null,
            you: { ...view.you, mustChoose: false },
          },
        }
      : state.end;
    current = { last: view, end };
    setState(current);
  }

  const end = current.end;
  const gameOver = end !== null && end.next === null;
  useEffect(() => {
    if (!gameOver) return;
    const timer = setTimeout(
      () => setState((s) => (s.end?.next === null ? { ...s, end: null } : s)),
      anim(GAME_END_HOLD_MS),
    );
    return () => clearTimeout(timer);
  }, [gameOver]);
  return {
    view: end?.frozen ?? view,
    roundEnd: end,
    clearRoundEnd: () => setState((s) => ({ ...s, end: null })),
  };
}

/**
 * Full-screen announcements: round over / the next round (big banner, until Next) and the end of
 * the game. 'Round Over' shows when a round ends (see `useRoundEnd`); if cards had to be chosen
 * first, the next round is announced with its dealer at its first reveal (a war, or play), before
 * anything flips. Rounds already under way when the table mounts (join, reload) aren't announced.
 * `banner`: a round banner is up. (The Beginner Mode lessons show after it: see `lessons/`.)
 */
interface InterstitialProps {
  view: GameView;
  roundEnd: RoundEnd | null;
  onRoundEndDone: () => void;
  /** Auto Next Round option: round banners close on their own (`AUTO_NEXT_MS`). */
  autoNext: boolean;
  onLeave: () => void;
  /** An attack, war reveal or dice roll is playing: no screen may start now. */
  busy: boolean;
  /** Game over waits (the Beginner ghost lesson for the turn that ended the game is pending). */
  holdEnd: boolean;
  /** Records you set this game (they arrive just after it ends), or null. */
  records: readonly GameRecord[] | null;
  /** Game over: open (or join) the rematch. */
  onRematch: () => void;
  /** Game over: open the game's recap. */
  onRecap: () => void;
}

/** `want`, but it may only start while nothing is `busy`; once up it stays up while `want` holds. */
function useUnlessBusy(want: boolean, busy: boolean): boolean {
  const [up, setUp] = useState(false);
  const show = want && (up || !busy);
  if (show !== up) setUp(show);
  return show;
}

/** The screen to show now, or null. Nothing starts while `busy`; a screen already up stays. */
function useInterstitialScreen({
  view,
  roundEnd,
  onRoundEndDone,
  autoNext,
  onLeave,
  busy,
  holdEnd,
  records,
  onRematch,
  onRecap,
}: InterstitialProps): { screen: ReactNode; banner: boolean } {
  const me = view.you.id;
  const name = (id: PlayerId | null) => view.players.find((p) => p.id === id)?.name ?? t('player.someone');
  // The round being revealed: during a war it is still numbered as the round before.
  const upcoming = view.phase === 'war' ? view.round + 1 : view.phase === 'playing' ? view.round : 0;
  const [seenRound, setSeenRound] = useState(upcoming);
  const [seenEnd, setSeenEnd] = useState(false);
  // A screen is up: it stays up even if something starts animating under it.
  const [up, setUp] = useState(false);
  const hold = busy && !up;

  // The round-end banner already named the dealer: don't announce the same round again.
  if (roundEnd?.next && roundEnd.dealerId !== null && roundEnd.next > seenRound) setSeenRound(roundEnd.next);

  const shownEnd = hold ? null : roundEnd;
  const showRound = !hold && !roundEnd && upcoming > seenRound;

  // Sounds: Round Over when its banner appears; a round starts when its own banner appears, or
  // when a Round Over banner that already announced it ends.
  useEffect(() => {
    if (shownEnd) playSound('roundEnd');
  }, [shownEnd]);
  useEffect(() => {
    if (showRound) playSound('roundStart');
  }, [showRound, upcoming]);
  const prevEnd = useRef(shownEnd);
  useEffect(() => {
    if (prevEnd.current?.next && prevEnd.current.dealerId !== null && !shownEnd) playSound('roundStart');
    prevEnd.current = shownEnd;
  }, [shownEnd]);

  const dealerLine = (dealerId: PlayerId | null) =>
    dealerId === null ? null : (
      <p className="splash-dealer">
        <img src="/img/dealer-button.png" alt="" className="splash-chip" />
        {/* One span: in the flex line the name would otherwise be its own box, not part of the sentence. */}
        <span>
          {dealerId === me ? t('round.dealerYou') : tx('round.dealer', { name: <PName>{name(dealerId)}</PName> })}
        </span>
      </p>
    );

  const result = hold ? { screen: null, banner: false } : pickScreen();
  if (!!result.screen !== up) setUp(!!result.screen);
  return result;

  function pickScreen(): { screen: ReactNode; banner: boolean } {
    if (roundEnd) {
      // A game that just ended holds the final table without a banner, then shows game over.
      if (roundEnd.next === null) return { screen: null, banner: false };
      return {
        screen: (
          <RoundBanner key="end" auto={autoNext} onClose={onRoundEndDone}>
            <h2>{t('round.over')}</h2>
            {dealerLine(roundEnd.dealerId)}
          </RoundBanner>
        ),
        banner: true,
      };
    }

    if (view.phase === 'finished' && !seenEnd && !holdEnd) {
      const won = view.winnerId === me;
      const lost = !won && !!view.winnerId && !view.you.spectating;
      const screen = (
        <Splash onClose={() => setSeenEnd(true)}>
          <h2 className={won ? 'winner' : 'loser'}>{won ? t('end.win') : lost ? t('end.lose') : t('end.over')}</h2>
          <p>
            {won
              ? t('end.winBody')
              : view.winnerId
                ? tx('end.winner', { name: <PName>{name(view.winnerId)}</PName> })
                : t('end.noSurvivors')}
          </p>
          {records && <GameRecords records={records} />}
          {/* Stacked, all one width. */}
          <div className="actions end-actions">
            <button onClick={() => setSeenEnd(true)}>{t('end.viewTable')}</button>
            <button onClick={onRecap}>{t('end.recap')}</button>
            <button onClick={onLeave}>{t('table.backToLobby')}</button>
            {view.players.some((p) => p.id === me) ? (
              <button className="primary" onClick={onRematch}>
                {t(view.rematchId ? 'end.joinRematch' : 'end.rematch')}
              </button>
            ) : null}
          </div>
        </Splash>
      );
      return { screen, banner: false };
    }

    if (showRound) {
      return {
        screen: (
          <RoundBanner key={`round:${upcoming}`} auto={autoNext} onClose={() => setSeenRound(upcoming)}>
            <h2>{t('round.title', { round: upcoming })}</h2>
            {dealerLine(view.dealerId)}
          </RoundBanner>
        ),
        banner: true,
      };
    }

    return { screen: null, banner: false };
  }
}

/** Auto Next Round: how long a round banner stays before it closes itself. Fixed (Fast Animations doesn't shorten it). */
const AUTO_NEXT_MS = 1_000;

/** Full-screen round announcement; stays until Next, or with `auto` closes itself after `AUTO_NEXT_MS` (no Next button). */
function RoundBanner({ auto, onClose, children }: { auto: boolean; onClose: () => void; children: ReactNode }) {
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  useEffect(() => {
    if (!auto) return;
    const timer = setTimeout(() => close.current(), AUTO_NEXT_MS);
    return () => clearTimeout(timer);
  }, [auto]);
  return (
    <div className="round-banner" role="status">
      {children}
      {!auto && (
        <button className="primary intro-next" onClick={onClose}>
          {t('common.next')}
        </button>
      )}
    </div>
  );
}

/** Game over: the records you set this game (new #1s first). */
function GameRecords({ records }: { records: readonly GameRecord[] }) {
  const sorted = [...records].sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'top' ? -1 : 1));
  return (
    <div className="game-records">
      <h4>{t('end.records')}</h4>
      <ul>
        {sorted.map((r) => (
          <li key={`${r.kind}:${r.metric}`} className={r.kind}>
            <span className="record-kind">{t(r.kind === 'top' ? 'end.recordTop' : 'end.recordBest')}</span>
            <span>{metricName(r.metric)}</span>
            <span className="record-value">{formatMetric(r.metric, r.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Splash({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal splash" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function phaseTitle(view: GameView): string {
  switch (view.phase) {
    case 'lobby':
      return t('phase.lobby', { n: view.players.length });
    case 'choosing':
      return t('phase.choosing');
    case 'war':
      return t('phase.war');
    case 'playing':
      if (view.turn?.ghost) return t('phase.ghost', { round: view.round });
      return t('phase.playing', {
        round: view.round,
        turn: Math.min(view.sequence, MAX_SEQUENCES),
        turns: MAX_SEQUENCES,
      });
    case 'finished':
      return t('phase.finished');
  }
}

/** Status for the middle of the table. Your own turn controls live in your area, not here. */
function PhasePanel({
  client,
  view,
  botLevel,
  rollFx,
}: Props & {
  botLevel: BotLevel;
  rollFx: RollFx | null;
}) {
  const me = view.you.id;
  const name = (id: PlayerId | null) => view.players.find((p) => p.id === id)?.name ?? t('player.someone');
  const waitingOn = view.waitingFor.filter((id) => id !== me);
  const waitingLine =
    waitingOn.length > 0 ? (
      <p className="muted">
        {tx('table.waitingTutorial', {
          names: txList(waitingOn.map((id) => <PName key={id}>{name(id)}</PName>)),
        })}
      </p>
    ) : null;

  switch (view.phase) {
    case 'lobby': {
      const isHost = view.ownerId === me;
      const controls = isHost ? (
        <>
          <OptionTable className="lobby-controls">
            <OptionRow label={t('lobby.watchOnly')} hint={t('lobby.watchOnlyHint')} htmlFor="lobby-watch-only">
              <input
                id="lobby-watch-only"
                type="checkbox"
                checked={view.you.spectating}
                disabled={view.you.spectating && view.players.length >= MAX_PLAYERS}
                onChange={(e) => void client.request('lobby:watchOnly', { on: e.target.checked })}
              />
            </OptionRow>
            <OptionRow label={t('lobby.botLevel')} hint={t('lobby.botLevelHint')} htmlFor="lobby-bot-level">
              <AddBot client={client} full={view.players.length >= MAX_PLAYERS} defaultLevel={botLevel} />
            </OptionRow>
          </OptionTable>
          <div className="row actions">
            <button
              className="primary"
              disabled={view.players.length < MIN_PLAYERS}
              onClick={() => void client.request('game:start')}
            >
              {t('lobby.start')}
            </button>
          </div>
        </>
      ) : (
        <p className="muted">{tx('lobby.waitingHost', { name: <PName>{name(view.ownerId)}</PName> })}</p>
      );
      return (
        <div className="lobby-panel">
          <div className="row lobby-invite">
            {view.quickFillAt && <QuickFillCountdown at={view.quickFillAt} />}
            <span className="spacer" />
            <InviteButton client={client} gameId={view.gameId} />
          </div>
          {!view.you.spectating && <ColorPicker client={client} view={view} />}
          <GameOptions client={client} view={view} />
          {controls}
        </div>
      );
    }

    case 'choosing': {
      if (view.you.mustChoose) return <p>{t('choosing.prompt')}</p>;
      const waiting = view.players.filter((p) => !p.hasCard && p.status !== 'out');
      return (
        <p className="muted">
          {tx('choosing.waiting', {
            names:
              waiting.length === 0
                ? t('choosing.everyone')
                : txList(waiting.map((p) => <PName key={p.id}>{p.name}</PName>)),
          })}
        </p>
      );
    }

    case 'war':
      return (
        <>
          {waitingLine}
          <WarRoll client={client} view={view} />
        </>
      );

    case 'playing': {
      const turn = view.turn;
      if (!turn) return null;
      if (waitingLine) return waitingLine;
      if (turn.ghost) return <GhostPanel client={client} view={view} rollFx={rollFx} />;
      return turn.playerId === me ? (
        <p className="your-turn">{t('table.yourTurn')}</p>
      ) : (
        <p className="muted">{tx('table.theirTurn', { name: <PName>{name(turn.playerId)}</PName> })}</p>
      );
    }

    case 'finished':
      return (
        <p className="winner">
          {view.winnerId ? tx('end.winsBang', { name: <PName>{name(view.winnerId)}</PName> }) : t('end.noSurvivors')}
        </p>
      );
  }
}

/** Lobby: share this game's join link (the share sheet on phones, else copied). */
function InviteButton({ client, gameId }: { client: GameClient; gameId: number }) {
  const share = async () => {
    const result = await shareLink(siteLink(`/join/${gameId}`), t('lobby.inviteTitle'));
    if (result === 'copied') client.notify({ kind: 'success', text: t('lobby.inviteCopied') });
    if (result === 'failed') client.notify({ kind: 'error', text: t('lobby.inviteFailed') });
  };
  return (
    <button onClick={() => void share()} title={t('lobby.inviteHint')}>
      {t('lobby.invite')}
    </button>
  );
}

/** Quick Play lobby: seconds until it fills with bots and starts (unless another human joins). */
function QuickFillCountdown({ at }: { at: string }) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);
  const seconds = Math.max(0, Math.ceil((Date.parse(at) - now) / 1000));
  return <span className="muted quick-fill">{t('lobby.quickFill', { n: seconds })}</span>;
}

/** Lobby: Add Bot with its level. Starts at your Options default; a change sticks for the next bot. */
function AddBot({ client, full, defaultLevel }: { client: GameClient; full: boolean; defaultLevel: BotLevel }) {
  const [level, setLevel] = useState<BotLevel>(defaultLevel);
  return (
    <span className="add-bot">
      <select id="lobby-bot-level" value={level} disabled={full} onChange={(e) => setLevel(e.target.value as BotLevel)}>
        {BOT_LEVELS.map((l) => (
          <option key={l} value={l} title={t(`botLevel.${l}.hint`)}>
            {t(`botLevel.${l}`)}
          </option>
        ))}
      </select>
      <button
        disabled={full}
        title={t('lobby.addBotHint', { level: t(`botLevel.${level}`) })}
        onClick={() => void client.request('lobby:addBot', { level })}
      >
        {t('lobby.addBot')}
      </button>
    </span>
  );
}

/** Lobby: the host's game options (card selection, ghost rolls, deck, idle limit); everyone else sees them read-only. */
function GameOptions({ client, view }: Props) {
  const settings = view.settings;
  const disabled = view.ownerId !== view.you.id;
  const set = (change: Partial<typeof settings>) => void client.request('lobby:settings', { ...settings, ...change });
  return (
    <fieldset className="game-options">
      <legend>{t('lobby.gameOptions')}</legend>
      <OptionTable>
        <OptionRow label={t('lobby.cardSelection')} hint={t('lobby.cardSelectionHint')} htmlFor="game-card-selection">
          <select
            id="game-card-selection"
            value={settings.cardSelection}
            disabled={disabled}
            onChange={(e) => set({ cardSelection: e.target.value as (typeof CARD_SELECTIONS)[number] })}
          >
            {CARD_SELECTIONS.map((c) => (
              <option key={c} value={c}>
                {t(`lobby.cardSelection.${c}`)}
              </option>
            ))}
          </select>
        </OptionRow>
        <OptionRow label={t('lobby.ghostRolls')} hint={t('lobby.ghostRollsHint')} htmlFor="game-ghost-rolls">
          <select
            id="game-ghost-rolls"
            value={settings.ghostRolls ? 'yes' : 'no'}
            disabled={disabled}
            onChange={(e) => set({ ghostRolls: e.target.value === 'yes' })}
          >
            <option value="yes">{t('lobby.yes')}</option>
            <option value="no">{t('lobby.no')}</option>
          </select>
        </OptionRow>
        <OptionRow label={t('lobby.botCollusion')} hint={t('lobby.botCollusionHint')} htmlFor="game-bot-collusion">
          <select
            id="game-bot-collusion"
            value={settings.botCollusion ? 'yes' : 'no'}
            disabled={disabled}
            onChange={(e) => set({ botCollusion: e.target.value === 'yes' })}
          >
            <option value="yes">{t('lobby.yes')}</option>
            <option value="no">{t('lobby.no')}</option>
          </select>
        </OptionRow>
        <OptionRow label={t('lobby.deck')} hint={t('lobby.deckHint')} htmlFor="game-deck">
          <select
            id="game-deck"
            value={settings.deck}
            disabled={disabled}
            onChange={(e) => set({ deck: e.target.value as (typeof DECKS)[number] })}
          >
            {DECKS.map((d) => (
              <option key={d} value={d}>
                {t(`lobby.deck.${d}`)}
              </option>
            ))}
          </select>
        </OptionRow>
        <OptionRow label={t('lobby.idleLimit')} hint={t('lobby.idleLimitHint')} htmlFor="game-idle-limit">
          <select
            id="game-idle-limit"
            value={settings.idleLimit}
            disabled={disabled}
            onChange={(e) => set({ idleLimit: Number(e.target.value) as (typeof IDLE_LIMITS)[number] })}
          >
            {IDLE_LIMITS.map((m) => (
              <option key={m} value={m}>
                {m === 0 ? t('lobby.idleLimit.off') : idleLimitText(m)}
              </option>
            ))}
          </select>
        </OptionRow>
      </OptionTable>
    </fieldset>
  );
}

/** Your war action tray: just the Roll button, while you are a fighter who hasn't rolled. The dice sit under the cards. */
function WarRoll({ client, view }: Props) {
  const war = view.war;
  const me = view.you.id;
  const arm = useMotionArm();
  if (!war || !war.participants.includes(me) || war.rolls[me] || view.waitingFor.length > 0) return null;
  const roll = () =>
    arm
      ? arm({
          count: Math.min(war.round, MAX_WAR_DICE),
          send: async (toss) => (await client.request('war:roll', { toss })) !== undefined,
        })
      : void client.request('war:roll', {});
  return (
    <button className={`primary${view.learn ? ' coach-pulse' : ''}`} onClick={roll}>
      {t('war.roll')}
    </button>
  );
}

/**
 * A player's card on the table (or back / empty / dead / safe), with the dealer chip on its
 * corner. `strip`: only its power window (circle, frame and Kill Quest banner) shows, the seat's
 * width, with the Kill Quest's short name over the banner (a turn's small seats, both layouts).
 */
function CardFace({
  player: p,
  card,
  isDealer,
  tooltip,
  onZoom,
  strip = false,
}: {
  player: PublicPlayer;
  card: CardDef | undefined;
  isDealer: boolean;
  tooltip: string | undefined;
  /** Open the enlarged card (a click anywhere on the card). */
  onZoom?: () => void;
  strip?: boolean;
}) {
  // The skull is for ghosts only (out of cards); a killed card with cards left in hand shows the deck.
  const dead = p.card === null && !p.hasCard && p.status !== 'active' && p.status !== 'safe' && p.handCount === 0;
  const art =
    p.card !== null
      ? cardImage(p.color, p.card)
      : p.hasCard || p.status === 'active' || (p.status !== 'safe' && p.handCount > 0)
        ? cardImage(p.color, 'back')
        : p.status === 'safe'
          ? '/img/safe.png'
          : '/img/dead.png';
  // The skull art isn't card-shaped: shown whole, and centred on the skull in a cropped seat.
  const artClass = dead ? 'card dead' : 'card';

  return (
    // `data-card-of`: this player's card on the table (their seat, whatever its size), used by
    // the animations and intros.
    <div
      className={`card-face${strip ? ' strip' : ''}${onZoom ? ' zoomable' : ''}`}
      data-card-of={p.id}
      onClick={
        onZoom &&
        ((e) => {
          e.stopPropagation();
          onZoom();
        })
      }
      role={onZoom ? 'button' : undefined}
      tabIndex={onZoom ? 0 : undefined}
      aria-label={onZoom ? t('card.enlargeLabel', { name: card?.name ?? t('card.alt') }) : undefined}
      onKeyDown={
        onZoom &&
        ((e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.stopPropagation();
          onZoom();
        })
      }
    >
      {strip ? (
        <div className="strip-window">
          {art && <img src={art} alt={card?.name ?? t('card.alt')} title={tooltip} className={artClass} />}
          {/* The Kill Quest's short name, over the one printed on the art (too small to read in a slug). */}
          {card && p.card !== null && <span className="quest-label">{t(`killquest_shortname.${card.killer}`)}</span>}
        </div>
      ) : art ? (
        <img src={art} alt={card?.name ?? t('card.alt')} title={tooltip} className={artClass} />
      ) : (
        <div className="card empty" />
      )}
      {isDealer && (
        <img src="/img/dealer-button.png" alt={t('table.dealer')} title={t('table.dealer')} className="dealer-chip" />
      )}
    </div>
  );
}

/**
 * A player's hand as a face-down deck peeking out up and to the left of their card: one back
 * per card, up to four, with the count as a badge on its top-left corner. Click yours to look at it.
 */
function DeckStack({
  color,
  count,
  onClick,
  hideCount = false,
}: {
  color: DeckColor | null;
  count: number;
  onClick: (() => void) | undefined;
  /** Lobby: just the look of a full deck, no count. */
  hideCount?: boolean;
}) {
  if (count === 0) return null;
  const shown = Math.min(count, 4);
  return (
    <button
      className="deck-stack"
      onClick={
        onClick &&
        ((e) => {
          e.stopPropagation();
          onClick();
        })
      }
      disabled={!onClick}
      title={hideCount ? undefined : onClick ? tn('deck.title', count) : tn('deck.count', count)}
      aria-label={hideCount ? undefined : onClick ? t('deck.label', { n: count }) : tn('deck.count', count)}
    >
      {Array.from({ length: shown }, (_, i) => (
        <img
          key={i}
          src={cardImage(color, 'back')}
          alt=""
          className="card"
          style={{ '--depth': shown - i } as CSSProperties}
        />
      ))}
      {!hideCount && (
        <span className="deck-count" style={{ '--depth': shown } as CSSProperties}>
          {count}
        </span>
      )}
    </button>
  );
}

/** Lobby: a player's chosen deck, face down — a full stack of that color's card backs. */
function LobbyDeck({ color }: { color: DeckColor | null }) {
  return (
    <div className="card-stack">
      <DeckStack color={color} count={4} onClick={undefined} hideCount />
      <div className="card-face">
        <img
          src={cardImage(color, 'back')}
          alt={color ? t('deck.colorAlt', { color: t(`color.${color}`) }) : t('deck.alt')}
          title={color ? t(`color.${color}`) : undefined}
          className="card"
        />
      </div>
    </div>
  );
}

/** Lobby: pick your deck color. Colors other players have are greyed out. */
function ColorPicker({ client, view }: Props) {
  const mine = view.players.find((p) => p.id === view.you.id)?.color;
  return (
    <div className="color-picker" role="group" aria-label={t('lobby.deckColor')}>
      {DECK_COLORS.map((c) => {
        const owner = view.players.find((p) => p.color === c && p.id !== view.you.id);
        return (
          <button
            key={c}
            disabled={!!owner}
            aria-pressed={mine === c}
            title={owner ? t('lobby.colorTaken', { color: t(`color.${c}`), name: owner.name }) : t(`color.${c}`)}
            onClick={() => mine !== c && void client.request('lobby:chooseColor', { color: c })}
          >
            <img src={cardImage(c, 'back')} alt={t('deck.colorAlt', { color: t(`color.${c}`) })} />
          </button>
        );
      })}
    </div>
  );
}

/**
 * `normal`: a full seat; `strip`: a slug, cropped to the card's power window, the name over its
 * top and the Kill Quest's short name over its bottom, CSS pip dice, no deck; `big`: whoever plays a turn.
 */
/**
 * `name`: Portrait Layout, the big seat's place in the column: just its name row, glowing.
 * `spot`: Layout v1, the big seat's place in the slug row: a greyed slug, its name and Kill Quest only.
 *   Desktop (`full`, both layouts): a greyed full-size seat, its card-shaped place empty.
 */
type SeatSize = 'normal' | 'strip' | 'big' | 'name' | 'spot';

interface SeatProps {
  size: SeatSize;
  /** Layout v1, a turn over: the big seat shrinking into its spot (`useShrinkToSpot`), and that spot. */
  leaving?: boolean;
  /** Portrait Layout, a turn over: the room kept in the column for the big seat to glide into; unseen. */
  gap?: boolean;
  /** The big seat: what goes under the card in place of the seat's dice row (big dice, or your controls). */
  diceArea?: ReactNode;
  /** Portrait Layout's big seat: its name sits above the card, and no power line. */
  side?: boolean;
  /** Desktop: a `spot` is a full-size seat, not a slug. */
  full?: boolean;
  /** A war: fading out (not fighting, about to leave the layout) or back in (the war is over). */
  fade?: 'out' | 'in';
  player: PublicPlayer;
  isYou: boolean;
  card: CardDef | undefined;
  isTurn: boolean;
  isDealer: boolean;
  isWinner: boolean;
  /** Lobby: show the player's deck instead of a card. */
  inLobby: boolean;
  selected: boolean;
  /** Your kept dice can destroy this card. */
  killable: boolean;
  /** Beginner Mode: the dice on your table could destroy this card; its deck-colour flame burns behind it. */
  killFire?: boolean;
  /** Your power's free kills can destroy this card. */
  freeKillable: boolean;
  dice: readonly number[];
  /** The player is a fighter in the war being rolled: their dice show big under the card. */
  atWar: boolean;
  /** Indexes into `dice` still rolling (see `rolling.ts`). */
  rolling: readonly number[];
  /** Indexes into `dice` coloured as held. */
  held: readonly number[];
  /** Indexes into `dice` held dice being thrown back before the roll: unlocked, falling. */
  dropping?: readonly number[];
  /** Indexes into `dice` to show lifted (a bot's announced keep). */
  lifted: readonly number[] | undefined;
  /** Debug Mode: kill-odds context, or null when hidden. */
  odds: OddsContext | null;
  onOdds: () => void;
  onSelect: (() => void) | undefined;
  /** Host, in the lobby: remove this bot. */
  onRemove: (() => void) | undefined;
  /** Open this card enlarged (face-up cards only). */
  onZoom: (() => void) | undefined;
  /** Beginner mode: this player's held dice just went after your card; flash them. */
  targeting: boolean;
  /** Idle limit: seconds until this player forfeits and watches, while the countdown shows. */
  idleSeconds: number | undefined;
  /** Allow Bot Collusion: you have a deal with this player this round, or they broke one this game. */
  deal?: 'partner' | 'betrayer';
  /** The emote this player just sent, shown over the seat. */
  emote?: ShownEmote;
  /** In the game's voice chat, and talking right now. */
  voice?: 'in' | 'speaking';
  /** Your seat: cards in your hand (from your own view), and opening the hand viewer. */
  handCount: number | undefined;
  onViewHand: (() => void) | undefined;
}

function Seat({
  size,
  leaving = false,
  gap = false,
  diceArea,
  side = false,
  full = false,
  fade,
  player: p,
  isYou,
  card,
  isTurn,
  isDealer,
  isWinner,
  inLobby,
  selected,
  killable,
  freeKillable,
  killFire = false,
  dice,
  atWar,
  rolling,
  held,
  dropping,
  lifted,
  odds,
  onOdds,
  onSelect,
  onRemove,
  onZoom,
  targeting,
  idleSeconds,
  deal,
  emote,
  voice,
  handCount,
  onViewHand,
}: SeatProps) {
  const classes = ['seat', p.color ?? '', p.status];
  if (size !== 'normal') classes.push(size);
  if (isYou) classes.push('you');
  if (isTurn) classes.push('turn');
  if (isWinner) classes.push('winner');
  if (selected) classes.push('selected');
  if (killable) classes.push('killable');
  if (freeKillable) classes.push('free-kill');
  if (killFire) classes.push('kill-fire');
  if (onSelect) classes.push('selectable');
  if (targeting) classes.push('targeting');
  if (voice === 'speaking') classes.push('speaking');
  if (fade) classes.push(`fade-${fade}`);
  if (leaving) classes.push('leaving');
  if (gap) classes.push('gap');

  const name = (
    <div className="seat-name">
      {isYou && <span className="you-badge">{t('table.you')}</span>}
      <Avatar playerId={p.id} />
      <span>{p.name}</span>
      {voice && (
        <span className="in-voice" title={t('seat.inVoice')} aria-label={t('seat.inVoice')}>
          🎙
        </span>
      )}
      {deal && (
        <span
          className="deal-badge"
          title={t(deal === 'partner' ? 'deal.partnerBadge' : 'deal.betrayerBadge')}
          aria-label={t(deal === 'partner' ? 'deal.partnerBadge' : 'deal.betrayerBadge')}
        >
          {deal === 'partner' ? '🤝' : '🗡️'}
        </span>
      )}
      {!p.connected && (
        <img src="/img/connecting.gif" alt={t('seat.offline')} title={t('seat.offline')} className="offline" />
      )}
      {idleSeconds !== undefined && <IdlePill seconds={idleSeconds} />}
      {onRemove && (
        <button
          className="remove-bot"
          onClick={onRemove}
          aria-label={t('seat.removeBot', { name: p.name })}
          title={t('seat.removeBotTitle')}
        >
          ×
        </button>
      )}
    </div>
  );
  // The big seat's place in the column (`data-flip`: glides apart from the big seat, see `useSeatFlip`).
  if (size === 'name') {
    return (
      <div className={classes.join(' ')} data-seat-id={p.id} data-flip="name">
        {name}
      </div>
    );
  }
  // Same size as a slug so the row never shifts; no card (`data-card-of` stays on the big seat's).
  if (size === 'spot' && full) {
    return (
      <div
        className={`seat spot full-spot${leaving ? ' leaving' : ''} ${p.color ?? ''}`}
        data-seat-id={p.id}
        data-flip="spot"
      >
        {name}
        <div className="card-stack">
          <div className="spot-card">
            {card && p.card !== null && <span className="quest-label">{t(`killquest_shortname.${card.killer}`)}</span>}
            {!leaving && <span className="spot-rolling">{t('seat.rolling')}</span>}
          </div>
        </div>
        <div className="seat-dice-ph" aria-hidden>
          <Dice values={[1, 1, 1, 1, 1]} small />
        </div>
      </div>
    );
  }
  if (size === 'spot') {
    return (
      <div
        className={`seat strip spot${leaving ? ' leaving' : ''} ${p.color ?? ''}`}
        data-seat-id={p.id}
        data-flip="spot"
      >
        {name}
        <div className="card-stack">
          <div className="strip-window">
            {card && p.card !== null && <span className="quest-label">{t(`killquest_shortname.${card.killer}`)}</span>}
          </div>
          {/* Over the greyed window at full brightness, pulsing: this player is the one playing. */}
          {!leaving && <span className="spot-rolling">{t('seat.rolling')}</span>}
        </div>
        <div className="seat-dice-ph" aria-hidden>
          <Dice values={[1, 1, 1, 1, 1]} small pips />
        </div>
      </div>
    );
  }

  return (
    <div
      className={classes.join(' ')}
      data-seat-id={p.id}
      // Leaving: shrinks on its own (`useShrinkToSpot`), not glided by `useSeatFlip`. A gap is matched
      // apart from the big seat, which glides into the slug that replaces it.
      data-flip={leaving ? 'leaving' : gap ? 'gap' : undefined}
      aria-hidden={gap || undefined}
      onClick={onSelect}
      role={onSelect ? 'button' : undefined}
      aria-pressed={onSelect ? selected : undefined}
    >
      {/* Beginner Mode: your dice can destroy this card; the seat burns in its deck colour, behind everything. */}
      {killFire && p.color && <img className="kill-fire" src={fireSrc(p.color)} alt="" />}
      {name}
      {size === 'big' && !side && card && p.card !== null && <PowerLine card={card} className="seat-power" />}
      {inLobby ? (
        <LobbyDeck color={p.color} />
      ) : (
        <div className="card-stack">
          {size !== 'strip' && <DeckStack color={p.color} count={handCount ?? p.handCount} onClick={onViewHand} />}
          <CardFace
            player={p}
            card={card}
            isDealer={isDealer}
            tooltip={p.card === null ? undefined : cardTooltip(card)}
            // Picking targets to attack: a click on a target highlights it to kill instead.
            onZoom={onSelect ? undefined : onZoom}
            strip={size === 'strip'}
          />
          {(killable || freeKillable) && (
            <strong className={`card-hint ${killable ? 'kill-hint' : 'free-hint'}`}>
              {t(killable ? 'seat.canDestroy' : 'seat.freeKill')}
            </strong>
          )}
        </div>
      )}
      {inLobby && <div className="muted small">{t(p.bot ? 'seat.bot' : 'seat.ready')}</div>}
      {/* The dice row is always there (invisible placeholders when empty) so seats never change height. */}
      {diceArea ??
        (inLobby ? null : dice.length > 0 ? (
          atWar ? (
            // War rolls: big dice over the seat's dice row, hanging below it (out of the flow, so seats
            // keep their height), the total once landed.
            <div className="seat-dice-ph war-anchor">
              <Dice values={[1, 1, 1, 1, 1]} small />
              <div className="war-dice" style={{ '--war-n': dice.length } as CSSProperties}>
                <Dice values={dice} rolling={rolling} />
                {rolling.length === 0 && <strong className="war-total">{dice.reduce((a, b) => a + b, 0)}</strong>}
              </div>
            </div>
          ) : (
            <Dice
              values={dice}
              small
              held={held}
              dropping={dropping}
              lifted={lifted}
              rolling={rolling}
              pips={size === 'strip'}
            />
          )
        ) : (
          <div className="seat-dice-ph" aria-hidden>
            <Dice values={[1, 1, 1, 1, 1]} small pips={size === 'strip'} />
          </div>
        ))}
      {odds && <SeatOdds cx={odds} player={p} onOpen={onOdds} />}
      {emote && (
        <span key={emote.id} className="emote-bubble" aria-live="polite">
          {t(`emote.${emote.emote}`)}
        </span>
      )}
    </div>
  );
}

/**
 * The big seat's dice when it isn't your turn: five places in the order the dice fell (missing
 * dice outlined), big; a 🔒 under every die held (and under a bot's announced keep, lifted).
 */
function BigDice({
  values,
  rolling,
  held,
  dropping = NO_DICE,
  lifted,
  fire = null,
  burning = NO_DICE,
  steps,
}: {
  values: readonly number[];
  rolling: readonly number[];
  held: readonly number[];
  /** Held dice thrown back before the roll: shown unlocked, falling away (`useRollGate`). */
  dropping?: readonly number[];
  lifted: readonly number[] | undefined;
  /** Deck colour: the flame behind each die in `burning`. */
  fire?: DeckColor | null;
  /** Dice the player's power can change, while they decide (see `GameTable`'s `burningFor`). */
  burning?: readonly number[];
  /** A bot's dice change playing out: faces on the way, and the arrow flashing. */
  steps?: PlanSteps;
}) {
  return (
    <div className="dice-row">
      <div className="dice big dice-line">
        {Array.from({ length: DICE_COUNT }, (_, i) => {
          const v = steps?.value(i) ?? values[i] ?? 0;
          const flash = steps?.flash(i);
          const kept = !!lifted?.includes(i);
          const locked = v !== 0 && !rolling.includes(i) && (kept || held.includes(i));
          const dropped = v !== 0 && dropping.includes(i);
          return (
            <div key={i} className={`die-slot${v === 0 ? ' empty' : ''}`}>
              <div className="die-fx">
                {fire && v !== 0 && !rolling.includes(i) && burning.includes(i) && (
                  <img className="fire" src={fireSrc(fire)} alt="" />
                )}
                {flash && (
                  <span
                    key={flash.step}
                    className={`die-flash ${flash.dir}`}
                    style={{ animationDuration: `${anim(PLAN_STEP_MS)}ms` }}
                    aria-hidden
                  >
                    {flash.dir === 'up' ? '▲' : '▼'}
                  </span>
                )}
                <span
                  className={`die${v === 0 ? ' ghost' : ''}${rolling.includes(i) ? ' rolling' : ''}${kept ? ' kept' : ''}${dropped ? ' dropping' : ''}`}
                  role={v === 0 ? undefined : 'img'}
                  aria-hidden={v === 0 || undefined}
                  aria-label={
                    v === 0
                      ? undefined
                      : rolling.includes(i)
                        ? t('dice.rolling')
                        : t(
                            dropped
                              ? 'dice.droppedLabel'
                              : kept
                                ? 'dice.keptLabel'
                                : locked
                                  ? 'dice.heldLabel'
                                  : 'dice.showingLabel',
                            { value: v },
                          )
                  }
                >
                  <Pips value={v} rolling={rolling.includes(i)} />
                </span>
                {locked && (
                  <span className="die-lock" aria-hidden>
                    🔒
                  </span>
                )}
                {dropped && (
                  <span className="die-lock unlocking" aria-hidden>
                    🔓
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HandModal({
  title,
  color,
  hand,
  discards,
  catalog,
  onChoose,
  onClose,
}: {
  title: string;
  color: DeckColor | null;
  hand: CardId[];
  /** Your discard pile, shown below the hand (the hand viewer only). */
  discards?: CardId[];
  catalog: ReadonlyMap<CardId, CardDef>;
  onChoose?: (cardId: CardId) => void;
  onClose?: () => void;
}) {
  const cardButton = (id: CardId, i: number, faded: boolean) => {
    const card = catalog.get(id);
    return (
      <button
        key={`${id}-${i}`}
        className="card-button"
        disabled={!onChoose}
        onClick={() => onChoose?.(id)}
        title={cardTooltip(card)}
      >
        <img
          src={cardImage(color, id)}
          alt={card?.name ?? t('card.altId', { id })}
          className={faded ? 'card discarded' : 'card'}
        />
      </button>
    );
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className={`modal hand${onChoose ? ' choosing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row">
          <h3>{title}</h3>
          <span className="spacer" />
          {onClose && <button onClick={onClose}>{t('common.close')}</button>}
        </div>
        <div className="hand-cards">{hand.map((id, i) => cardButton(id, i, false))}</div>
        {discards && (
          <section className="discard-pile">
            <h4>{t('hand.discardTitle')}</h4>
            {discards.length === 0 ? (
              <p className="muted">{t('hand.discardEmpty')}</p>
            ) : (
              <div className="hand-cards">{discards.map((id, i) => cardButton(id, i, true))}</div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

/**
 * Learn to Play: the note in the dice tray (your dice in your hand): these are Shake & Flick dice,
 * and Popple or Classic 2D are a tap away (kept as your Dice Type option).
 */
function DiceNote({ onPick }: { onPick: (type: DiceType) => void }) {
  return (
    <div className="dice-note">
      <strong>{t('learn.diceTitle')}</strong>
      <span>{t('learn.diceNote')}</span>
      <span className="row">
        <button onClick={() => onPick('popple')}>{t('learn.dicePopple')}</button>
        <button onClick={() => onPick('classic')}>{t('learn.diceClassic')}</button>
      </span>
    </div>
  );
}
